import { useLocation, useNavigate, Link } from 'react-router-dom';
import React, { useEffect, useState, useRef, useMemo } from "react";
import './LegalApp.css';
import Navbar from './Navbar';
import Footer from './Footer';
import ToggleSwitch from './ToggleSwitch';
import { getPreferredLang, SECTION_INFO_I18N, CONTINUE_PROMPT_I18N } from '../utils/sectionConfirmations';
import { FaFileWord, FaPencilAlt } from 'react-icons/fa';
import { useAnswerTranslation, getAnswerTranslation } from '../utils/answerTranslation';
import { AnswerLanguageSelect, TranslatedAnswerBox } from './AnswerTranslationFields';
import { hasAnyAnswers, buildQuestionsPlainText, buildQuestionsDocxBlocks, downloadFile, getUniqueAnswerDates, ALL_DATES_OPTION } from '../utils/questionsExport';
import { createStructuredDocxFile } from '../utils/docxBuilder';
import axios from 'axios';

const printTableSection = (elementId) => {
  // Get the element to print
  const element = document.getElementById(elementId);
  const title = element.id;

  if (!element) {
    console.error("Element not found");
    return;
  }

  // Create a new window
  const printWindow = window.open('', '_blank');

  // Write the element's content to the new window
  printWindow.document.write(`
    <html>
      <head>
        <title>LegalApp</title>
        <style>
          /* Add any custom styles for printing */
          table {
            width: 100%;
            border-collapse: collapse;
          }
          th, td {
            border: 1px solid #000;
            padding: 8px;
            text-align: left;
          }
        </style>
      </head>
      <body>
        <h3 style="text-align: center; font-size: 24px; font-weight: bold; font-family: 'Roboto', sans-serif;">FIR analysed using LegalApp</h3>
        <div style="text-align: center">
          <span style="text-align: center; font-size: 20px; font-weight: bold; font-family: 'Roboto', sans-serif;">www.legalapp.in</span>
        </div>
        <br></br>
        <h3 style="text-align: center; font-size: 24px; font-weight: bold; font-family: 'Roboto', sans-serif;">${title}</h3>
        <div style="font-family: 'Roboto', sans-serif;">
        ${element.innerHTML}
        </div>
      </body>
    </html>
  `);

  // Close the document to ensure it's ready for printing
  printWindow.document.close();

  // Trigger the print dialog
  printWindow.print();
};

const TableSection = ({ children }) => {
  const containerRef = useRef(null);
  useEffect(() => {
    // Scroll to left after short delay to allow rendering
    const timeout = setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.scrollLeft = 0;
      }
    }, 100); // 100ms is usually enough for render to complete

    return () => clearTimeout(timeout);
  }, []);

  return (
    <div className="table-section" ref={containerRef}>
      {children}
    </div>
  );
};

const FIRSections = ({ sections }) => {
  return (
    <TableSection title="">
      <div class="fir-table-container">
        <table className='fir-table'>
          <thead>
            <tr>
              <th>S. No.</th>
              <th>Section</th>
              <th>Act</th>
            </tr>
          </thead>
          <tbody>
            {sections.map((section, index) => (
              <tr key={index}>
                <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: '15px', width: "4%" }}>{index + 1}</td>
                <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: '15px', width: "20%" }}>{section.number}</td>
                <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: '15px' }}>{section.act}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </TableSection>
  );
};

const ComplainantTable = ({ complainant }) => {
  return (
    <TableSection title="">
      <div class="fir-table-container">
        <table className="fir-table">
          <thead>
            <tr>
              <th>S. No.</th>
              <th>Name</th>
              <th>Age</th>
              <th>Gender</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: '15px', width: "4%" }}>1</td>
              <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: '15px', width: "20%" }}>{complainant.name}</td>
              <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: '15px', width: "15%" }}>{complainant.age}</td>
              <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: '15px' }}>{complainant.gender}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </TableSection>
  );
};

const VictimTable = ({ victim }) => {
  return (
    <TableSection title="">
      <div class="fir-table-container">
        <table className="fir-table">
          <thead>
            <tr>
              <th>S. No.</th>
              <th>Name</th>
              <th>Age</th>
              <th>Gender</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: '15px', width: "4%" }}>1</td>
              <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: '15px', width: "20%" }}>{victim && victim.name ? victim.name : 'Unknown'}</td>
              <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: '15px', width: "15%" }}>{victim && victim.age ? victim.age : 'Unknown'}</td>
              <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: '15px' }}>{victim && victim.gender ? victim.gender : 'Unknown'}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </TableSection>
  );
};

const AccusedTable = ({ accusedList }) => {
  return (
    <TableSection title="">
      <div class="fir-table-container">
        <table className="fir-table">
          <thead>
            <tr>
              <th>S. No.</th>
              <th>Name</th>
              <th>Age</th>
              <th>Gender</th>
              <th>Is Alive</th>
              <th>Relationship with Complainant</th>
            </tr>
          </thead>
          <tbody>
            {accusedList.map((accused, index) => (
              <tr key={index}>
                <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: '15px', width: "4%" }}>{index + 1}</td>
                <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: '15px', width: "20%" }}>{accused.name}</td>
                <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: '15px', width: "15%" }}>{accused.age}</td>
                <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: '15px', width: "15%" }}>{accused.gender}</td>
                <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: '15px', width: "15%" }}>{accused.deceased === "No" ? "Yes" : "No"}</td>
                <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: '15px' }}>{accused.relationshipWithComplainant}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </TableSection>
  );
};

const EventsTable = ({ fir }) => {
  return (
    <TableSection title="">
      <div class="fir-table-container">
        <table className="fir-table">
          <thead>
            <tr>
              <th>S. No.</th>
              <th>Description</th>
              <th>Date</th>
              <th>Time</th>
              <th>Location</th>
              <th>Persons Involved</th>
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            {fir.eventsAndTimeline && fir.eventsAndTimeline.map((event, index) => {
              return (
                <tr key={index}>
                  <td style={{ verticalAlign: "top", paddingTop: "15px", width: "4%" }}>
                    {index + 1}
                  </td>
                  <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: "15px", width: "20%" }}>
                    {event.description || "-"}
                  </td>
                  <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: "15px", width: "15%" }}>
                    {event.date || "-"}
                  </td>
                  <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: "15px", width: "15%" }}>
                    {event.time || "-"}
                  </td>
                  <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: "15px", width: "15%" }}>
                    {event.location || "-"}
                  </td>
                  <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: "15px" }}>
                    {event.personsInvolved ? event.personsInvolved.join(', ') : "-"}
                  </td>
                  <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: "15px" }}>
                    {event.source ? event.source : "-"}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </TableSection>
  );
};

const TASK_PRIORITY_TO_CATEGORY = { Top: "Level 1", High: "Level 2", Medium: "Level 3", Low: "Level 4" };

const UrgentTasksTable = ({ API_URL, fir, updateFir }) => {
  const [statusByKey, setStatusByKey] = useState({});

  const [showAddTask, setShowAddTask] = useState(false);
  const [newTaskDescription, setNewTaskDescription] = useState("");
  const [newTaskNote, setNewTaskNote] = useState("");
  const [newTaskUrgency, setNewTaskUrgency] = useState("");
  const [newTaskPriority, setNewTaskPriority] = useState("");
  const [addTaskLoading, setAddTaskLoading] = useState(false);
  const [addTaskError, setAddTaskError] = useState(null);

  const closeAddTask = () => {
    setShowAddTask(false);
    setNewTaskDescription("");
    setNewTaskNote("");
    setNewTaskUrgency("");
    setNewTaskPriority("");
    setAddTaskError(null);
  };

  const submitNewTask = async () => {
    setAddTaskLoading(true);
    setAddTaskError(null);

    const jwt = localStorage.getItem("jwtToken");
    try {
      const response = await axios.post(
        API_URL + "/fir/task",
        {
          firId: fir.id,
          description: newTaskDescription.trim(),
          note: newTaskNote.trim(),
          urgency: newTaskUrgency.trim(),
          category: TASK_PRIORITY_TO_CATEGORY[newTaskPriority],
        },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwt}` } }
      );
      updateFir(response.data);
      closeAddTask();
    } catch {
      setAddTaskError("Failed to add task. Please try again.");
    } finally {
      setAddTaskLoading(false);
    }
  };

  const getTaskKey = (task, index) => (task.id ? String(task.id) : `${task.description || "task"}__${index}`);

  const isTaskCompleted = (task, index) => {
    const key = getTaskKey(task, index);
    return key in statusByKey ? statusByKey[key] : !!task.status;
  };

  const toggleTaskStatus = async (task, index) => {
    const key = getTaskKey(task, index);
    const prev = isTaskCompleted(task, index);
    setStatusByKey((s) => ({ ...s, [key]: !prev }));

    const jwt = localStorage.getItem("jwtToken");
    try {
      const response = await axios.post(
        API_URL + `/fir/tasks/status/${fir.id}`,
        { task: task.description, status: !prev ? "completed" : "pending" },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwt}` } }
      );
      if (response.status === 200) updateFir(response.data);
    } catch {
      setStatusByKey((s) => ({ ...s, [key]: prev }));
    }
  };

  const getTaskPriority = (task) => {
    switch (task.category) {
      case "Level 1":
        return "Top";
      case "Level 2":
        return "High";
      case "Level 3":
        return "Medium";
      default:
        return "Low";
    }
  };

  const priorityOrder = {
    Top: 1,
    High: 2,
    Medium: 3,
    Low: 4,
  };

  // Tabs in explicit order
  const tabs = ["Top", "High", "Medium", "Low"];

  // Default to Top tab
  const [selectedTab, setSelectedTab] = useState("Top");

  // Prepare + sort once
  const sortedTasks = useMemo(() => {
    return (fir.urgentTasks || [])
      .map((task, originalIndex) => ({
        ...task,
        priority: getTaskPriority(task),
        __originalIndex: originalIndex,
      }))
      .sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
  }, [fir]);

  const tabCounts = useMemo(() => {
    const counts = { Top: 0, High: 0, Medium: 0, Low: 0 };
    for (const t of sortedTasks) {
      counts[t.priority] = (counts[t.priority] || 0) + 1;
    }
    return counts;
  }, [sortedTasks]);

  // Filter by tab
  const filteredTasks = useMemo(() => {
    return sortedTasks.filter((t) => t.priority === selectedTab);
  }, [sortedTasks, selectedTab]);

  return (
    <TableSection title="">
      <div className="fir-table-container">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            clear: "both",
          }}
        >
          {/* Tabs + Add Task */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "12px",
              flexWrap: "wrap",
              width: "100%",
              clear: "both",
              gap: "10px",
            }}
          >
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
              {tabs.map((tab) => {
                const isActive = tab === selectedTab;
                const count = tabCounts[tab] ?? 0;

                return (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setSelectedTab(tab)}
                    style={{
                      padding: "8px 12px",
                      borderRadius: "999px",
                      border: isActive ? "1px solid #2563eb" : "1px solid #d1d5db",
                      background: isActive ? "#2563eb" : "#ffffff",
                      color: isActive ? "#ffffff" : "#111827",
                      fontWeight: isActive ? 700 : 500,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span>{tab}</span>

                    {/* Count badge */}
                    <span
                      style={{
                        minWidth: "22px",
                        height: "22px",
                        padding: "0 6px",
                        borderRadius: "999px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        fontWeight: 700,
                        lineHeight: 1,
                        background: isActive ? "#ffffff" : "grey",  // contrasting
                        color: isActive ? "#2563eb" : "#ffffff",
                      }}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              onClick={() => setShowAddTask(true)}
              style={{ backgroundColor: "#007bff", color: "white", border: "none", borderRadius: "6px", padding: "8px 14px", cursor: "pointer", fontWeight: 600 }}
            >
              + Add Task
            </button>
          </div>

          <table className="fir-table">
            <thead>
              <tr>
                <th>S. No.</th>
                <th>SOP</th>
                <th>Note (where / from whom)</th>
                <th>Relevance</th>
                <th>Source</th>
                <th>Status</th>
                {/* <th>Priority</th> */}
              </tr>
            </thead>

            <tbody>
              {filteredTasks.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: "16px", textAlign: "center" }}>
                    No tasks in <b>{selectedTab}</b>.
                  </td>
                </tr>
              ) : (
                filteredTasks.map((task, index) => {
                  const isCompleted = isTaskCompleted(task, task.__originalIndex);
                  return (
                    <tr key={index}>
                      <td style={{ verticalAlign: "top", paddingTop: "15px", width: "4%" }}>
                        {index + 1}
                      </td>

                      <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: "15px", width: "20%" }}>
                        {task.description || "-"}
                      </td>

                      <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: "15px", width: "15%" }}>
                        {task.note || "-"}
                      </td>

                      <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: "15px", width: "15%" }}>
                        {task.urgency || "-"}
                      </td>

                      <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: "15px", width: "15%" }}>
                        {task.source || "FIR"}
                      </td>

                      <td style={{ verticalAlign: "top", paddingTop: "15px", width: "10%" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <ToggleSwitch checked={isCompleted} onChange={() => toggleTaskStatus(task, task.__originalIndex)} />
                          <span style={{ fontSize: "13px", color: isCompleted ? "#4CAF50" : "#E53935" }}>
                            {isCompleted ? "Completed" : "Pending"}
                          </span>
                        </div>
                      </td>
                      {/*
                    <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: "15px", width: "15%" }}>
                      {task.priority}
                    </td> */}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showAddTask && (
        <div className="popup-overlay">
          <div className="popup-container" style={{ maxWidth: "520px", width: "92%" }}>
            <h3>Add Task</h3>

            <textarea
              placeholder="Description"
              value={newTaskDescription}
              onChange={(e) => setNewTaskDescription(e.target.value)}
              rows={3}
              style={{ width: "100%", boxSizing: "border-box", borderRadius: "6px", border: "1px solid #ccc", padding: "10px", marginBottom: "12px", fontFamily: "inherit" }}
            />

            <textarea
              placeholder="Note (where / from whom)"
              value={newTaskNote}
              onChange={(e) => setNewTaskNote(e.target.value)}
              rows={2}
              style={{ width: "100%", boxSizing: "border-box", borderRadius: "6px", border: "1px solid #ccc", padding: "10px", marginBottom: "12px", fontFamily: "inherit" }}
            />

            <textarea
              placeholder="Relevance"
              value={newTaskUrgency}
              onChange={(e) => setNewTaskUrgency(e.target.value)}
              rows={2}
              style={{ width: "100%", boxSizing: "border-box", borderRadius: "6px", border: "1px solid #ccc", padding: "10px", marginBottom: "12px", fontFamily: "inherit" }}
            />

            <div style={{ fontWeight: 600, paddingTop: "6px", paddingBottom: "10px", textAlign: "left" }}>Priority</div>
            <div style={{ marginBottom: "12px", display: "flex", alignItems: "flex-start", gap: "20px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", flex: 1 }}>
                {tabs.map((p) => (
                  <label key={p} style={{ display: "flex", alignItems: "center", gap: "8px", width: "100%", boxSizing: "border-box", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={newTaskPriority === p}
                      onChange={() => setNewTaskPriority(newTaskPriority === p ? "" : p)}
                      style={{ width: "16px", height: "16px", margin: 0, flexShrink: 0 }}
                    />
                    {p}
                  </label>
                ))}
              </div>
            </div>

            {addTaskError && <p style={{ color: "#c9302c" }}>{addTaskError}</p>}

            <div className="popup-buttons">
              <button onClick={closeAddTask} className="cancel-button" disabled={addTaskLoading}>Cancel</button>
              <button
                onClick={submitNewTask}
                className="submit-popup-button"
                disabled={addTaskLoading || !newTaskDescription.trim() || !newTaskPriority}
              >
                {addTaskLoading ? <div className="popup-btn-spinner"></div> : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}
    </TableSection>
  );
};

const EvidenceTable = ({ API_URL, fir, updateFir }) => {
  const [statusByKey, setStatusByKey] = useState({});

  const [showAddEvidence, setShowAddEvidence] = useState(false);
  const [newEvidenceDescription, setNewEvidenceDescription] = useState("");
  const [newEvidenceRelevance, setNewEvidenceRelevance] = useState("");
  const [newEvidencePriority, setNewEvidencePriority] = useState("");
  const [addEvidenceLoading, setAddEvidenceLoading] = useState(false);
  const [addEvidenceError, setAddEvidenceError] = useState(null);

  const closeAddEvidence = () => {
    setShowAddEvidence(false);
    setNewEvidenceDescription("");
    setNewEvidenceRelevance("");
    setNewEvidencePriority("");
    setAddEvidenceError(null);
  };

  const submitNewEvidence = async () => {
    setAddEvidenceLoading(true);
    setAddEvidenceError(null);

    const jwt = localStorage.getItem("jwtToken");
    try {
      const response = await axios.post(
        API_URL + "/fir/evidence",
        {
          firId: fir.id,
          description: newEvidenceDescription.trim(),
          relevance: newEvidenceRelevance.trim(),
          category: TASK_PRIORITY_TO_CATEGORY[newEvidencePriority],
        },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwt}` } }
      );
      updateFir(response.data);
      closeAddEvidence();
    } catch {
      setAddEvidenceError("Failed to add evidence. Please try again.");
    } finally {
      setAddEvidenceLoading(false);
    }
  };

  const isEvidenceCompleted = (key) =>
    key in statusByKey
      ? !!statusByKey[key]
      : String(fir?.evidenceStatus?.[key] || "").toLowerCase() === "completed";

  const toggleEvidenceStatus = async (key) => {
    const prev = isEvidenceCompleted(key);
    setStatusByKey((s) => ({ ...s, [key]: !prev }));

    const jwt = localStorage.getItem("jwtToken");
    try {
      const response = await axios.post(
        API_URL + `/fir/evidences/status/${fir.id}`,
        { evidence: key, status: !prev ? "completed" : "pending" },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwt}` } }
      );
      if (response.status === 200) updateFir(response.data);
    } catch {
      setStatusByKey((s) => ({ ...s, [key]: prev }));
    }
  };

  const getEvidenceCategory = (key) => {
    const level = fir?.evidenceCategory?.[key];
    if (level === "Level 1") return "Top";
    if (level === "Level 2") return "High";
    if (level === "Level 3") return "Medium";
    return "Low";
  };

  const PRIORITY_ORDER = { Top: 0, High: 1, Medium: 2, Low: 3 };
  const tabs = ["Top", "High", "Medium", "Low"];

  const [selectedTab, setSelectedTab] = useState("Top");

  const evidenceRows = useMemo(() => {
    return Object.entries(fir?.evidenceList ?? {})
      .map(([key, value]) => ({
        key,
        value,
        source: fir?.evidenceSource?.[key] ?? "FIR",
        priority: getEvidenceCategory(key),
      }))
      .sort((a, b) => PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]);
  }, [fir]);

  // ✅ counts per tab
  const tabCounts = useMemo(() => {
    const counts = { Top: 0, High: 0, Medium: 0, Low: 0 };
    for (const row of evidenceRows) {
      counts[row.priority] = (counts[row.priority] || 0) + 1;
    }
    return counts;
  }, [evidenceRows]);

  // ✅ filter by selected tab
  const filteredRows = useMemo(() => {
    return evidenceRows.filter((r) => r.priority === selectedTab);
  }, [evidenceRows, selectedTab]);

  return (
    <TableSection title="">
      <div className="fir-table-container">
        {/* Force vertical stacking even if parent has flex/float styles */}
        <div style={{ display: "flex", flexDirection: "column", width: "100%", clear: "both" }}>
          {/* Tabs + Add Evidence */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "12px",
              flexWrap: "wrap",
              width: "100%",
              clear: "both",
              gap: "10px",
            }}
          >
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
              {tabs.map((tab) => {
                const isActive = tab === selectedTab;
                const count = tabCounts[tab] ?? 0;

                return (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setSelectedTab(tab)}
                    style={{
                      padding: "8px 12px",
                      borderRadius: "999px",
                      border: isActive ? "1px solid #2563eb" : "1px solid #d1d5db",
                      background: isActive ? "#2563eb" : "#ffffff",
                      color: isActive ? "#ffffff" : "#111827",
                      fontWeight: isActive ? 700 : 500,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span>{tab}</span>

                    {/* Count badge */}
                    <span
                      style={{
                        minWidth: "22px",
                        height: "22px",
                        padding: "0 6px",
                        borderRadius: "999px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        fontWeight: 700,
                        lineHeight: 1,
                        background: isActive ? "#ffffff" : "grey",
                        color: isActive ? "#2563eb" : "#ffffff",
                      }}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              onClick={() => setShowAddEvidence(true)}
              style={{ backgroundColor: "#007bff", color: "white", border: "none", borderRadius: "6px", padding: "8px 14px", cursor: "pointer", fontWeight: 600 }}
            >
              + Add Evidence
            </button>
          </div>

          {/* Table */}
          <table className="fir-table" style={{ width: "100%", float: "none", clear: "both" }}>
            <thead>
              <tr>
                <th>S. No.</th>
                <th>Evidence</th>
                <th>Source</th>
                <th>Status</th>
                {/* <th>Priority</th> */}
              </tr>
            </thead>

            <tbody>
              {filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ padding: "16px", textAlign: "center" }}>
                    No evidence in <b>{selectedTab}</b>.
                  </td>
                </tr>
              ) : (
                filteredRows.map((row, index) => {
                  const isCompleted = isEvidenceCompleted(row.key);
                  return (
                    <tr key={row.key}>
                      <td
                        style={{
                          textAlign: "justify",
                          verticalAlign: "top",
                          paddingTop: "15px",
                          width: "4%",
                        }}
                      >
                        {index + 1}
                      </td>

                      <td
                        style={{
                          textAlign: "justify",
                          verticalAlign: "top",
                          paddingTop: "15px",
                          width: "70%",
                        }}
                      >
                        <strong>{row.key}</strong>
                        <br />
                        <span style={{ color: "grey", fontSize: "15px" }}>
                          {row.value}
                        </span>
                      </td>

                      <td>
                        <span
                          style={{
                            textAlign: "justify",
                            verticalAlign: "top",
                            paddingTop: "15px",
                            display: "inline-block",
                          }}
                        >
                          {row.source}
                        </span>
                      </td>

                      <td style={{ verticalAlign: "top", paddingTop: "15px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <ToggleSwitch checked={isCompleted} onChange={() => toggleEvidenceStatus(row.key)} />
                          <span style={{ fontSize: "13px", color: isCompleted ? "#4CAF50" : "#E53935" }}>
                            {isCompleted ? "Completed" : "Pending"}
                          </span>
                        </div>
                      </td>

                      {/* <td>
                      <span
                        style={{
                          textAlign: "justify",
                          verticalAlign: "top",
                          paddingTop: "20px",
                          display: "inline-block",
                        }}
                      >
                        {row.priority}
                      </span>
                    </td> */}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showAddEvidence && (
        <div className="popup-overlay">
          <div className="popup-container" style={{ maxWidth: "520px", width: "92%" }}>
            <h3>Add Evidence</h3>

            <textarea
              placeholder="Description"
              value={newEvidenceDescription}
              onChange={(e) => setNewEvidenceDescription(e.target.value)}
              rows={3}
              style={{ width: "100%", boxSizing: "border-box", borderRadius: "6px", border: "1px solid #ccc", padding: "10px", marginBottom: "12px", fontFamily: "inherit" }}
            />

            <textarea
              placeholder="Relevance"
              value={newEvidenceRelevance}
              onChange={(e) => setNewEvidenceRelevance(e.target.value)}
              rows={3}
              style={{ width: "100%", boxSizing: "border-box", borderRadius: "6px", border: "1px solid #ccc", padding: "10px", marginBottom: "12px", fontFamily: "inherit" }}
            />

            <div style={{ fontWeight: 600, whiteSpace: "nowrap", paddingTop: "6px", textAlign: "left", paddingBottom: "8px" }}>Priority</div>
            <div style={{ marginBottom: "12px", display: "flex", alignItems: "flex-start", gap: "20px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", flex: 1 }}>
                {tabs.map((p) => (
                  <label key={p} style={{ display: "flex", alignItems: "center", gap: "8px", width: "100%", boxSizing: "border-box", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={newEvidencePriority === p}
                      onChange={() => setNewEvidencePriority(newEvidencePriority === p ? "" : p)}
                      style={{ width: "16px", height: "16px", margin: 0, flexShrink: 0 }}
                    />
                    {p}
                  </label>
                ))}
              </div>
            </div>

            {addEvidenceError && <p style={{ color: "#c9302c" }}>{addEvidenceError}</p>}

            <div className="popup-buttons">
              <button onClick={closeAddEvidence} className="cancel-button" disabled={addEvidenceLoading}>Cancel</button>
              <button
                onClick={submitNewEvidence}
                className="submit-popup-button"
                disabled={addEvidenceLoading || !newEvidenceDescription.trim() || !newEvidenceRelevance.trim()}
              >
                {addEvidenceLoading ? <div className="popup-btn-spinner"></div> : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}
    </TableSection>
  );
};

const AccusedIQTable = ({ API_URL, fir: defaultFIR, updateFir }) => {
  const [fir, setFir] = useState(defaultFIR);
  const [confirmRequest, setConfirmRequest] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const [accused, setAccused] = useState(null);
  const [questionStatusByKey, setQuestionStatusByKey] = useState({});

  /* ---------------- Update Name popup (per accused) ---------------- */

  const [namePopup, setNamePopup] = useState(null); // { accusedObj }
  const [newNameInput, setNewNameInput] = useState("");
  const [isSavingName, setIsSavingName] = useState(false);
  const [nameError, setNameError] = useState(null);

  const openNamePopup = (accusedObj) => {
    setNamePopup({ accusedObj });
    setNewNameInput(accusedObj.name || "");
    setNameError(null);
  };

  const closeNamePopup = () => {
    setNamePopup(null);
    setNewNameInput("");
    setNameError(null);
  };

  const saveName = async () => {
    if (!namePopup || !newNameInput.trim()) return;

    setIsSavingName(true);
    setNameError(null);

    const jwt = localStorage.getItem("jwtToken");
    try {
      const response = await axios.post(
        API_URL + `/fir/name/${fir.id}`,
        {
          user: "Accused",
          existingName: namePopup.accusedObj.name,
          newName: newNameInput.trim(),
        },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwt}` } }
      );
      setFir(response.data);
      updateFir(response.data);
      closeNamePopup();
    } catch {
      setNameError("Failed to update the name. Please try again.");
    } finally {
      setIsSavingName(false);
    }
  };

  const answerTranslation = useAnswerTranslation((path, body) =>
    axios.post(API_URL + path, body, {
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("jwtToken")}` },
    })
  );

  const [answerPopup, setAnswerPopup] = useState(null); // { accusedObj, question }
  const [answerText, setAnswerText] = useState("");
  const [isSavingAnswer, setIsSavingAnswer] = useState(false);
  const [answerError, setAnswerError] = useState(null);

  const openAnswerPopup = (accusedObj, question) => {
    setAnswerPopup({ accusedObj, question });
    setAnswerText("");
    setAnswerError(null);
    answerTranslation.reset();
  };

  const closeAnswerPopup = () => {
    setAnswerPopup(null);
    setAnswerText("");
    setAnswerError(null);
    answerTranslation.reset();
  };

  const saveAnswer = async () => {
    if (!answerPopup || !answerText.trim()) return;

    setIsSavingAnswer(true);
    setAnswerError(null);

    const jwt = localStorage.getItem("jwtToken");
    try {
      const answerTranslationText = await answerTranslation.ensureTranslation(answerText);
      const response = await axios.post(
        API_URL + `/fir/iq/answer/${fir.id}`,
        {
          user: "Accused",
          name: answerPopup.accusedObj.name,
          question: answerPopup.question.question,
          answer: answerText.trim(),
          answerLanguage: answerTranslation.answerLanguage,
          answerTranslationLanguage: answerTranslation.translationLanguage,
          answerTranslation: answerTranslationText,
        },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwt}` } }
      );
      setFir(response.data);
      updateFir(response.data);
      closeAnswerPopup();
    } catch (err) {
      // A failed translation already shows its own message next to the translation box.
      if (err?.message !== "translate failed") setAnswerError("Failed to save the answer. Please try again.");
    } finally {
      setIsSavingAnswer(false);
    }
  };

  const [showAddAccused, setShowAddAccused] = useState(false);
  const [newAccusedGender, setNewAccusedGender] = useState("");
  const [newAccusedName, setNewAccusedName] = useState("");
  const [newAccusedRole, setNewAccusedRole] = useState("");
  const [newAccusedRelationship, setNewAccusedRelationship] = useState("");
  const [addAccusedLoading, setAddAccusedLoading] = useState(false);
  const [addAccusedError, setAddAccusedError] = useState(null);

  const closeAddAccused = () => {
    setShowAddAccused(false);
    setNewAccusedGender("");
    setNewAccusedName("");
    setNewAccusedRole("");
    setNewAccusedRelationship("");
    setAddAccusedError(null);
  };

  const submitNewAccused = async () => {
    setAddAccusedLoading(true);
    setAddAccusedError(null);

    const jwt = localStorage.getItem("jwtToken");
    try {
      const response = await axios.post(
        API_URL + "/fir/accused",
        {
          firId: fir.id,
          gender: newAccusedGender,
          name: newAccusedName.trim(),
          role: newAccusedRole.trim(),
          relationshipWithComplainant: newAccusedRelationship.trim(),
        },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwt}` } }
      );
      setFir(response.data);
      updateFir(response.data);
      closeAddAccused();
    } catch {
      setAddAccusedError("Failed to add accused. Please try again.");
    } finally {
      setAddAccusedLoading(false);
    }
  };

  const tabs = ["Top", "High", "Medium", "Low"];
  const [selectedTabByAccused, setSelectedTabByAccused] = useState({});

  const getAccusedKey = (a) => a?.id || a?.name; // use id if available

  const isQuestionCompleted = (accusedKey, question) => {
    const key = `${accusedKey}__${question.question}`;
    return key in questionStatusByKey ? questionStatusByKey[key] : !!question.status;
  };

  const toggleQuestionStatus = async (accusedObj, accusedKey, question) => {
    const key = `${accusedKey}__${question.question}`;
    const prev = isQuestionCompleted(accusedKey, question);
    setQuestionStatusByKey((s) => ({ ...s, [key]: !prev }));

    const jwt = localStorage.getItem("jwtToken");
    try {
      const response = await axios.post(
        API_URL + `/fir/iq/status/${fir.id}`,
        { user: "Accused", name: accusedObj.name, question: question.question, status: !prev ? "completed" : "pending" },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwt}` } }
      );
      if (response.status === 200) {
        setFir(response.data);
        updateFir(response.data);
      }
    } catch {
      setQuestionStatusByKey((s) => ({ ...s, [key]: prev }));
    }
  };

  const computeTabCounts = (sortedQuestions) => {
    const counts = { Top: 0, High: 0, Medium: 0, Low: 0 };
    for (const q of sortedQuestions) {
      counts[q.priorityLabel] = (counts[q.priorityLabel] || 0) + 1;
    }
    return counts;
  };

  const getDefaultTabForCounts = (counts) => {
    for (const t of tabs) {
      if ((counts[t] || 0) > 0) return t;
    }
    return "Top";
  };

  const onClickGenerate = (accusedObj) => {
    setAccused(accusedObj);
    setConfirmRequest(true);
  };

  const onClose = () => {
    setAccused(null);
    setErrorMessage(null);
    setIsLoading(false);
    setConfirmRequest(false);
  };

  const handleConfirm = async () => {
    setIsLoading(true);

    const jwt = localStorage.getItem("jwtToken");
    const request = { name: accused.name };

    try {
      const response = await axios.post(
        API_URL + `/fir/accused/iq/${fir.id}`,
        request,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );
      setFir(response.data);
      updateFir(response.data);
    } catch (error) {
      setErrorMessage(
        "An error occurred while processing the request. Please try again later."
      );
    } finally {
      setIsLoading(false);
      setConfirmRequest(false);
    }
  };

  /* ---------------- Priority helpers ---------------- */

  const getQuestionPriority = (category) => {
    switch (category) {
      case "Level 1":
        return "Top";
      case "Level 2":
        return "High";
      case "Level 3":
        return "Medium";
      default:
        return "Low";
    }
  };

  const priorityOrder = { Top: 1, High: 2, Medium: 3, Low: 4 };

  const sortQuestionsByPriority = (questions) => {
    if (!Array.isArray(questions)) return [];
    return questions
      .map((q) => ({
        ...q,
        priorityLabel: getQuestionPriority(q.category),
      }))
      .sort(
        (a, b) => priorityOrder[a.priorityLabel] - priorityOrder[b.priorityLabel]
      );
  };

  /* ---------------- Copy popup (per accused) ---------------- */

  const [copyPopupOpen, setCopyPopupOpen] = useState(false);
  const [copyForAccusedKey, setCopyForAccusedKey] = useState(null);
  const [copySelected, setCopySelected] = useState({
    Top: true,
    High: true,
    Medium: true,
    Low: true,
  });
  const [copyFeedback, setCopyFeedback] = useState(null);
  const [copyBtnMode, setCopyBtnMode] = useState("copy"); // "copy" | "done"
  const [copyIncludeAnswers, setCopyIncludeAnswers] = useState(true);

  const closeCopyPopup = () => {
    setCopyPopupOpen(false);
    setCopyFeedback(null);
    setCopyBtnMode("copy");
    setCopyForAccusedKey(null);
  };

  const openCopyPopupForAccused = (accusedKey) => {
    setCopySelected({ Top: true, High: true, Medium: true, Low: true });
    setCopyIncludeAnswers(true);
    setCopyFeedback(null);
    setCopyBtnMode("copy");
    setCopyForAccusedKey(accusedKey);
    setCopyPopupOpen(true);
  };

  const getSelectedQuestions = (accusedObj, selectedMap) => {
    const selectedPriorities = tabs.filter((t) => !!selectedMap[t]);

    const qs = Array.isArray(accusedObj?.interrogationQuestions)
      ? accusedObj.interrogationQuestions
      : [];

    const sorted = sortQuestionsByPriority(qs);

    return sorted.filter((q) => selectedPriorities.includes(q.priorityLabel) && q?.question);
  };

  const handleCopyForAccused = async (accusedObj) => {
    try {
      const picked = getSelectedQuestions(accusedObj, copySelected);
      const text = buildQuestionsPlainText(picked, copyIncludeAnswers);
      if (!text.trim()) {
        setCopyFeedback("No questions match the selected categories.");
        return;
      }

      await navigator.clipboard.writeText(text);

      // Show tick-mark, then close after 2 seconds
      setCopyFeedback(null);
      setCopyBtnMode("done");

      window.setTimeout(() => {
        closeCopyPopup();
      }, 2000);
    } catch (e) {
      setCopyFeedback("Failed to copy. Please try again.");
    }
  };

  const getAccusedObjByKey = (accusedKey) => {
    const list = Array.isArray(fir?.accusedList) ? fir.accusedList : [];
    return list.find((a) => getAccusedKey(a) === accusedKey) || null;
  };

  /* ---------------- Download popup (per accused) ---------------- */

  const [downloadPopupOpen, setDownloadPopupOpen] = useState(false);
  const [downloadForAccusedKey, setDownloadForAccusedKey] = useState(null);
  const [downloadSelected, setDownloadSelected] = useState({
    Top: true,
    High: true,
    Medium: true,
    Low: true,
  });
  const [downloadIncludeAnswers, setDownloadIncludeAnswers] = useState(true);
  const [downloadDateWise, setDownloadDateWise] = useState(false);
  const [downloadSelectedDate, setDownloadSelectedDate] = useState(ALL_DATES_OPTION);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  const openDownloadPopupForAccused = (accusedKey) => {
    setDownloadSelected({ Top: true, High: true, Medium: true, Low: true });
    setDownloadIncludeAnswers(true);
    setDownloadDateWise(false);
    setDownloadSelectedDate(ALL_DATES_OPTION);
    setDownloadError(null);
    setDownloadForAccusedKey(accusedKey);
    setDownloadPopupOpen(true);
  };

  const closeDownloadPopup = () => {
    setDownloadPopupOpen(false);
    setDownloadError(null);
    setDownloadForAccusedKey(null);
  };

  const handleDownloadForAccused = async (accusedObj) => {
    const picked = getSelectedQuestions(accusedObj, downloadSelected);

    if (picked.length === 0) {
      setDownloadError("No questions match the selected categories.");
      return;
    }

    const dateWiseActive = downloadIncludeAnswers && downloadDateWise;

    if (dateWiseActive) {
      const uniqueDates = getUniqueAnswerDates(picked);
      if (downloadSelectedDate === ALL_DATES_OPTION && uniqueDates.length === 0) {
        setDownloadError("No dated answers found for the selected categories.");
        return;
      }
      if (downloadSelectedDate !== ALL_DATES_OPTION && !uniqueDates.some((d) => d.label === downloadSelectedDate)) {
        setDownloadError("No answered questions match the selected date.");
        return;
      }
    }

    setIsDownloading(true);
    setDownloadError(null);

    try {
      const blocks = buildQuestionsDocxBlocks({
        heading: `Interrogation of ${accusedObj.name}`,
        questions: picked,
        includeAnswers: downloadIncludeAnswers,
        dateWise: dateWiseActive,
        selectedDate: downloadSelectedDate,
      });
      const file = await createStructuredDocxFile(blocks, `${accusedObj.name}_Interrogation_Questions.docx`);
      downloadFile(file);
      closeDownloadPopup();
    } catch (e) {
      setDownloadError("Failed to generate the Word document. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  /* -------------------------------------------------- */

  return (
    <TableSection title="">
      <div className="fir-table-container">
        <div style={{ display: "flex", flexDirection: "column", width: "100%", clear: "both" }}>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "10px" }}>
            <button
              type="button"
              onClick={() => setShowAddAccused(true)}
              style={{ backgroundColor: "#007bff", color: "white", border: "none", borderRadius: "6px", padding: "8px 14px", cursor: "pointer", fontWeight: 600 }}
            >
              + Add Accused
            </button>
          </div>

          <table className="fir-table">
            <thead>
              <tr>
                <th>S. No.</th>
                <th>Accused</th>
                <th>Source</th>
                <th>Interrogation Questions</th>
              </tr>
            </thead>

            <tbody>
              {fir.accusedList &&
                fir.accusedList.map((accusedObj, index) => {
                  const questions = Array.isArray(accusedObj?.interrogationQuestions)
                    ? accusedObj.interrogationQuestions
                    : [];

                  const hasQuestions = questions.length > 0;
                  const sortedQuestions = sortQuestionsByPriority(questions);

                  return (
                    <tr key={index}>
                      {/* Serial Number */}
                      <td
                        style={{
                          verticalAlign: "top",
                          paddingTop: "15px",
                          width: "4%",
                        }}
                      >
                        {index + 1}
                      </td>

                      {/* Accused Details */}
                      <td
                        style={{
                          textAlign: "justify",
                          verticalAlign: "top",
                          paddingTop: "15px",
                          width: "26%",
                        }}
                      >
                        <div>
                          <strong>Name:</strong> {accusedObj.name}{" "}
                          <button
                            type="button"
                            onClick={() => openNamePopup(accusedObj)}
                            title="Edit name"
                            aria-label="Edit name"
                            style={{ background: "none", border: "none", padding: 0, cursor: "pointer", verticalAlign: "middle" }}
                          >
                            <FaPencilAlt size={13} color="#6b7280" />
                          </button>
                        </div>
                      </td>

                      {/* Source */}
                      <td
                        style={{
                          textAlign: "justify",
                          verticalAlign: "top",
                          paddingTop: "15px",
                          width: "15%",
                        }}
                      >
                        <div>{accusedObj.source || "FIR"}</div>
                      </td>

                      {/* Interrogation Questions */}
                      <td
                        style={{
                          textAlign: "justify",
                          verticalAlign: "top",
                          paddingTop: "15px",
                          width: "70%",
                        }}
                      >
                        {hasQuestions ? (
                          (() => {
                            const accusedKey = getAccusedKey(accusedObj);
                            const tabCounts = computeTabCounts(sortedQuestions);

                            const selectedTab =
                              selectedTabByAccused[accusedKey] ??
                              getDefaultTabForCounts(tabCounts);

                            const filteredQuestions = sortedQuestions.filter(
                              (q) => q.priorityLabel === selectedTab
                            );

                            return (
                              <>
                                {/* First row inside cell: Tabs on left, Copy on right */}
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "10px",
                                    marginBottom: "12px",
                                    width: "100%",
                                    clear: "both",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  {/* Tabs */}
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: "10px",
                                      flexWrap: "wrap",
                                      alignItems: "center",
                                    }}
                                  >
                                    {tabs.map((tab) => {
                                      const isActive = tab === selectedTab;
                                      const count = tabCounts[tab] ?? 0;

                                      return (
                                        <button
                                          key={tab}
                                          type="button"
                                          onClick={() =>
                                            setSelectedTabByAccused((prev) => ({
                                              ...prev,
                                              [accusedKey]: tab,
                                            }))
                                          }
                                          style={{
                                            padding: "6px 10px",
                                            borderRadius: "999px",
                                            border: isActive
                                              ? "1px solid #2563eb"
                                              : "1px solid #d1d5db",
                                            background: isActive ? "#2563eb" : "#ffffff",
                                            color: isActive ? "#ffffff" : "#111827",
                                            fontWeight: isActive ? 700 : 500,
                                            cursor: "pointer",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            fontSize: "13px",
                                          }}
                                        >
                                          <span>{tab}</span>

                                          <span
                                            style={{
                                              minWidth: "20px",
                                              height: "20px",
                                              padding: "0 6px",
                                              borderRadius: "999px",
                                              display: "inline-flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              fontSize: "11px",
                                              fontWeight: 800,
                                              lineHeight: 1,
                                              background: isActive ? "#ffffff" : "grey",
                                              color: isActive ? "#2563eb" : "#ffffff",
                                            }}
                                          >
                                            {count}
                                          </span>
                                        </button>
                                      );
                                    })}
                                  </div>

                                  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                    {/* Copy button */}
                                    <button
                                      type="button"
                                      onClick={() => openCopyPopupForAccused(accusedKey)}
                                      title="Copy questions for this accused"
                                      aria-label="Copy questions for this accused"
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        border: "1px solid #d1d5db",
                                        borderRadius: "8px",
                                        padding: "6px 10px",
                                        background: "#ffffff",
                                        cursor: "pointer",
                                        fontSize: "13px",
                                        fontWeight: 600,
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        aria-hidden="true"
                                      >
                                        <path
                                          d="M8 8V6.6C8 5.71634 8.71634 5 9.6 5H18.4C19.2837 5 20 5.71634 20 6.6V15.4C20 16.2837 19.2837 17 18.4 17H17"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                        />
                                        <path
                                          d="M4 8.6C4 7.71634 4.71634 7 5.6 7H14.4C15.2837 7 16 7.71634 16 8.6V19.4C16 20.2837 15.2837 21 14.4 21H5.6C4.71634 21 4 20.2837 4 19.4V8.6Z"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                        />
                                      </svg>
                                      <span>Copy</span>
                                    </button>

                                    {/* Download button */}
                                    <button
                                      type="button"
                                      onClick={() => openDownloadPopupForAccused(accusedKey)}
                                      title="Download questions for this accused"
                                      aria-label="Download questions for this accused"
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        border: "1px solid #d1d5db",
                                        borderRadius: "8px",
                                        padding: "6px 10px",
                                        background: "#ffffff",
                                        cursor: "pointer",
                                        fontSize: "13px",
                                        fontWeight: 600,
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      <FaFileWord style={{ fontSize: "16px", color: "#2B579A" }} />
                                      <span>Download</span>
                                    </button>
                                  </div>
                                </div>

                                {/* Questions filtered by selected tab */}
                                {filteredQuestions.length === 0 ? (
                                  <div style={{ color: "grey", fontSize: "14px", marginBottom: "12px" }}>
                                    No questions in <b>{selectedTab}</b>.
                                  </div>
                                ) : (
                                  filteredQuestions.map((q, i) => {
                                    const isCompleted = isQuestionCompleted(accusedKey, q);
                                    return (
                                      <div key={i} style={{ marginBottom: "15px" }}>
                                        <div>
                                          <strong>Question {i + 1}:</strong> {q.question}
                                        </div>

                                        <div
                                          style={{
                                            color: "grey",
                                            marginTop: "5px",
                                            fontSize: "15px",
                                          }}
                                        >
                                          {q.relevance}
                                        </div>

                                        {Array.isArray(q.answer) && q.answer.length > 0 && (
                                          <div style={{ marginTop: "5px", fontSize: "15px" }}>
                                            <strong>Answer:</strong>
                                            {q.answer.map((ans, idx) => (
                                              <div key={idx} style={{ marginTop: "4px" }}>
                                                {idx + 1}. {ans}
                                                {getAnswerTranslation(q, idx) && (
                                                  <div style={{ marginTop: "2px" }}>
                                                    <strong>Translation:</strong> {getAnswerTranslation(q, idx)}
                                                  </div>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        )}

                                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "12px", flexWrap: "wrap" }}>
                                          {!(Array.isArray(q.answer) && q.answer.length > 0) && (
                                            <button
                                              type="button"
                                              onClick={() => openAnswerPopup(accusedObj, q)}
                                              style={{
                                                border: "1px solid #367aef",
                                                borderRadius: "8px",
                                                padding: "4px 10px",
                                                marginRight: "20px",
                                                background: "#ffffff",
                                                cursor: "pointer",
                                                fontSize: "13px",
                                                fontWeight: 600,
                                                color: "#111827",
                                              }}
                                            >
                                              Record Answer
                                            </button>
                                          )}
                                          <ToggleSwitch
                                            checked={isCompleted}
                                            onChange={() => toggleQuestionStatus(accusedObj, accusedKey, q)}
                                          />
                                          <span style={{ fontSize: "13px", color: isCompleted ? "#4CAF50" : "#E53935" }}>
                                            {isCompleted ? "Completed" : "Pending"}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })
                                )}

                                {/* Regenerate Button */}
                                {localStorage.getItem("role") === "admin" && (
                                  <button
                                    style={{
                                      backgroundColor: "transparent",
                                      color: "#007BFF",
                                      border: "none",
                                      borderBottom: "1px solid #007BFF",
                                      padding: "4px 0 2px 0",
                                      cursor: "pointer",
                                      fontSize: "15px",
                                      fontWeight: "500",
                                    }}
                                    disabled={isLoading || confirmRequest}
                                    onClick={() => onClickGenerate(accusedObj)}
                                  >
                                    Regenerate Questions
                                  </button>
                                )}
                              </>
                            );
                          })()
                        ) : (
                          <button
                            style={{
                              backgroundColor: "#007bff",
                              color: "white",
                              border: "none",
                              borderRadius: "6px",
                              padding: "8px 14px",
                              cursor: "pointer",
                            }}
                            disabled={isLoading || confirmRequest}
                            onClick={() => onClickGenerate(accusedObj)}
                          >
                            Generate Questions
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Copy popup (per accused) */}
      {copyPopupOpen && (
        <div className="popup-overlay">
          <div className="popup-container" style={{ maxWidth: "520px", width: "92%" }}>
            <h3>Copy Interrogation Questions</h3>

            {/* Checkboxes in one row */}
            {/* Checkboxes in one row */}
            <div
              style={{
                display: "flex",
                gap: "16px",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                flexWrap: "nowrap",
                marginTop: "14px",
                marginBottom: "18px",
              }}
            >
              {tabs.map((label) => (
                <label
                  key={label}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    userSelect: "none",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!copySelected[label]}
                    onChange={(e) =>
                      setCopySelected((prev) => ({
                        ...prev,
                        [label]: e.target.checked,
                      }))
                    }
                    style={{ width: "14px", height: "14px", margin: 0 }}
                  />
                  {label}
                </label>
              ))}
            </div>

            {hasAnyAnswers(getAccusedObjByKey(copyForAccusedKey)?.interrogationQuestions) && (
              <div style={{ display: "flex", justifyContent: "center", marginBottom: "14px" }}>
                <label style={{ display: "inline-flex", alignItems: "center", gap: "8px", userSelect: "none", cursor: "pointer", fontSize: "14px", fontWeight: 600, whiteSpace: "nowrap" }}>
                  <input
                    type="checkbox"
                    checked={copyIncludeAnswers}
                    onChange={(e) => setCopyIncludeAnswers(e.target.checked)}
                    style={{ width: "14px", height: "14px", margin: 0 }}
                  />
                  Include Answers
                </label>
              </div>
            )}

            {copyFeedback && (
              <div style={{ color: "grey", fontSize: "14px", marginBottom: 10 }}>
                {copyFeedback}
              </div>
            )}

            <div className="popup-buttons">
              <button onClick={closeCopyPopup} className="cancel-button" disabled={copyBtnMode === "done"}>
                Cancel
              </button>

              <button
                onClick={() => {
                  const accusedObj = getAccusedObjByKey(copyForAccusedKey);
                  handleCopyForAccused(accusedObj);
                }}
                disabled={copyBtnMode === "done"
                }
                className="submit-popup-button"
              >
                {copyBtnMode === "done" ? (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "8px", fontWeight: 700 }}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M20 6L9 17L4 12"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Done
                  </span>
                ) : (
                  "Copy"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {downloadPopupOpen && (
        <div className="popup-overlay">
          <div className="popup-container" style={{ maxWidth: "520px", width: "92%" }}>
            <h3>Download Interrogation Questions</h3>

            <div
              style={{
                display: "flex",
                gap: "16px",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                flexWrap: "nowrap",
                marginTop: "14px",
                marginBottom: "18px",
              }}
            >
              {tabs.map((label) => (
                <label
                  key={label}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    userSelect: "none",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!downloadSelected[label]}
                    onChange={(e) =>
                      setDownloadSelected((prev) => ({
                        ...prev,
                        [label]: e.target.checked,
                      }))
                    }
                    style={{ width: "14px", height: "14px", margin: 0 }}
                  />
                  {label}
                </label>
              ))}
            </div>

            {(() => {
              const accusedObjForPopup = getAccusedObjByKey(downloadForAccusedKey);
              if (!hasAnyAnswers(accusedObjForPopup?.interrogationQuestions)) return null;

              const questionsForPopup = getSelectedQuestions(accusedObjForPopup, downloadSelected);
              const uniqueDates = getUniqueAnswerDates(questionsForPopup);

              return (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "16px", flexWrap: "wrap", marginBottom: "14px" }}>
                  <label style={{ display: "inline-flex", alignItems: "center", gap: "8px", userSelect: "none", cursor: "pointer", fontSize: "14px", fontWeight: 600, whiteSpace: "nowrap" }}>
                    <input
                      type="checkbox"
                      checked={downloadIncludeAnswers}
                      onChange={(e) => setDownloadIncludeAnswers(e.target.checked)}
                      style={{ width: "14px", height: "14px", margin: 0 }}
                    />
                    Include Answers
                  </label>

                  <label
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      userSelect: "none",
                      cursor: downloadIncludeAnswers ? "pointer" : "not-allowed",
                      fontSize: "14px",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      opacity: downloadIncludeAnswers ? 1 : 0.5,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={downloadDateWise}
                      disabled={!downloadIncludeAnswers}
                      onChange={(e) => setDownloadDateWise(e.target.checked)}
                      style={{ width: "14px", height: "14px", margin: 0 }}
                    />
                    Date Wise
                  </label>

                  <select
                    value={downloadSelectedDate}
                    disabled={!downloadIncludeAnswers || !downloadDateWise}
                    onChange={(e) => setDownloadSelectedDate(e.target.value)}
                    style={{ fontSize: "14px", padding: "4px 6px", position: "relative", top: "-4px", opacity: downloadIncludeAnswers && downloadDateWise ? 1 : 0.5 }}
                  >
                    <option value={ALL_DATES_OPTION}>{ALL_DATES_OPTION}</option>
                    {uniqueDates.map((d) => (
                      <option key={d.raw} value={d.label}>{d.label}</option>
                    ))}
                  </select>
                </div>
              );
            })()}

            {downloadError && (
              <div style={{ color: "#c9302c", fontSize: "14px", marginBottom: 10 }}>
                {downloadError}
              </div>
            )}

            <div className="popup-buttons">
              <button onClick={closeDownloadPopup} className="cancel-button" disabled={isDownloading}>
                Cancel
              </button>

              <button
                onClick={() => {
                  const accusedObj = getAccusedObjByKey(downloadForAccusedKey);
                  handleDownloadForAccused(accusedObj);
                }}
                disabled={isDownloading}
                className="submit-popup-button"
              >
                {isDownloading ? <div className="popup-btn-spinner"></div> : "Download"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Popup */}
      {errorMessage && (
        <div className="popup-overlay">
          <div className="error-popup">
            <div className="error-message">{errorMessage}</div>
            <button onClick={() => setErrorMessage(null)}>OK</button>
          </div>
        </div>
      )}

      {/* Confirm Popup */}
      {confirmRequest && (
        <div className="popup-overlay">
          <div className="popup-container">
            <h3>{(SECTION_INFO_I18N[getPreferredLang()] || SECTION_INFO_I18N.en).interrogations.title}</h3>
            <p style={{ textAlign: "justify" }}>
              {(SECTION_INFO_I18N[getPreferredLang()] || SECTION_INFO_I18N.en).interrogations.text}
              <br /><br />
              {CONTINUE_PROMPT_I18N[getPreferredLang()] || CONTINUE_PROMPT_I18N.en}
            </p>
            <div className="popup-buttons">
              <button onClick={onClose} className="cancel-button" disabled={isLoading}>
                Cancel
              </button>
              <button onClick={handleConfirm} className="submit-popup-button" disabled={isLoading}>
                {isLoading ? <div className="spinner"></div> : "Confirm"}
              </button>
            </div>
            {isLoading && (
              <div style={{ marginTop: "20px", fontWeight: "bold" }}>
                Please wait. It may take about a minute to do the analysis.
              </div>
            )}
          </div>
        </div>
      )}

      {showAddAccused && (
        <div className="popup-overlay">
          <div className="popup-container" style={{ maxWidth: "520px", width: "92%" }}>
            <h3>Add Accused</h3>

            <div style={{ marginBottom: "12px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
                <div style={{ fontWeight: 600, marginBottom: "8px" }}>Gender</div>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={newAccusedGender === "Male"}
                    onChange={() => setNewAccusedGender(newAccusedGender === "Male" ? "" : "Male")}
                    style={{ width: "16px", height: "16px", margin: 0 }}
                  />
                  Male
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={newAccusedGender === "Female"}
                    onChange={() => setNewAccusedGender(newAccusedGender === "Female" ? "" : "Female")}
                    style={{ width: "16px", height: "16px", margin: 0 }}
                  />
                  Female
                </label>
              </div>
            </div>

            <input
              placeholder="Name"
              value={newAccusedName}
              onChange={(e) => setNewAccusedName(e.target.value)}
              style={{ width: "100%", boxSizing: "border-box", borderRadius: "6px", border: "1px solid #ccc", padding: "10px", marginBottom: "12px" }}
            />

            <input
              placeholder="Role (crime committed by accused)"
              value={newAccusedRole}
              onChange={(e) => setNewAccusedRole(e.target.value)}
              style={{ width: "100%", boxSizing: "border-box", borderRadius: "6px", border: "1px solid #ccc", padding: "10px", marginBottom: "12px" }}
            />

            <textarea
              placeholder="Relation with Victim (optional)"
              value={newAccusedRelationship}
              onChange={(e) => setNewAccusedRelationship(e.target.value)}
              rows={2}
              style={{ width: "100%", boxSizing: "border-box", borderRadius: "6px", border: "1px solid #ccc", padding: "10px", marginBottom: "12px", fontFamily: "inherit" }}
            />

            {addAccusedError && <p style={{ color: "#c9302c" }}>{addAccusedError}</p>}

            <div className="popup-buttons">
              <button onClick={closeAddAccused} className="cancel-button" disabled={addAccusedLoading}>Cancel</button>
              <button
                onClick={submitNewAccused}
                className="submit-popup-button"
                disabled={addAccusedLoading || !newAccusedGender || !newAccusedName.trim() || !newAccusedRole.trim()}
              >
                {addAccusedLoading ? <div className="popup-btn-spinner"></div> : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}

      {answerPopup && (
        <div className="popup-overlay">
          <div className="popup-container" style={{ maxWidth: "520px", width: "92%" }}>
            <h3>Record Answer</h3>
            <p style={{ fontWeight: 600, marginBottom: "10px" }}>{answerPopup.question.question}</p>
            <AnswerLanguageSelect t={answerTranslation} disabled={isSavingAnswer} />
            <textarea
              value={answerText}
              onChange={(e) => setAnswerText(e.target.value)}
              rows={6}
              placeholder="Type the answer here."
              style={{ width: "100%", boxSizing: "border-box", borderRadius: "6px", border: "1px solid #ccc", padding: "10px", fontSize: "15px", fontFamily: "inherit", resize: "vertical" }}
            />
            <TranslatedAnswerBox t={answerTranslation} answerText={answerText} disabled={isSavingAnswer} />
            {answerError && <p style={{ color: "#c9302c" }}>{answerError}</p>}
            <div className="popup-buttons">
              <button onClick={closeAnswerPopup} className="cancel-button" disabled={isSavingAnswer}>Cancel</button>
              <button
                onClick={saveAnswer}
                className="submit-popup-button"
                disabled={isSavingAnswer || answerTranslation.isTranslating || !answerText.trim()}
              >
                {isSavingAnswer ? <div className="popup-btn-spinner"></div> : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {namePopup && (
        <div className="popup-overlay">
          <div className="popup-container" style={{ maxWidth: "420px", width: "92%" }}>
            <h3>Update Name</h3>
            <input
              type="text"
              value={newNameInput}
              onChange={(e) => setNewNameInput(e.target.value)}
              placeholder="Enter the updated name"
              style={{ width: "100%", boxSizing: "border-box", borderRadius: "6px", border: "1px solid #ccc", padding: "10px", fontSize: "15px", marginTop: "10px" }}
            />
            {nameError && <p style={{ color: "#c9302c", marginTop: "8px" }}>{nameError}</p>}
            <div className="popup-buttons">
              <button onClick={closeNamePopup} className="cancel-button" disabled={isSavingName}>Cancel</button>
              <button
                onClick={saveName}
                className="submit-popup-button"
                disabled={isSavingName || !newNameInput.trim()}
              >
                {isSavingName ? <div className="popup-btn-spinner"></div> : "Update"}
              </button>
            </div>
          </div>
        </div>
      )}
    </TableSection>
  );
};

const WitnessTable = ({ API_URL, fir: defaultFIR, updateFir }) => {
  const [fir, setFir] = useState(defaultFIR);
  const [confirmRequest, setConfirmRequest] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const [witness, setWitness] = useState(null);
  const [questionStatusByKey, setQuestionStatusByKey] = useState({});

  /* ---------------- Update Name popup (per witness) ---------------- */

  const [namePopup, setNamePopup] = useState(null); // { witnessObj }
  const [newNameInput, setNewNameInput] = useState("");
  const [isSavingName, setIsSavingName] = useState(false);
  const [nameError, setNameError] = useState(null);

  const openNamePopup = (witnessObj) => {
    setNamePopup({ witnessObj });
    setNewNameInput(witnessObj.name || "");
    setNameError(null);
  };

  const closeNamePopup = () => {
    setNamePopup(null);
    setNewNameInput("");
    setNameError(null);
  };

  const saveName = async () => {
    if (!namePopup || !newNameInput.trim()) return;

    setIsSavingName(true);
    setNameError(null);

    const jwt = localStorage.getItem("jwtToken");
    try {
      const response = await axios.post(
        API_URL + `/fir/name/${fir.id}`,
        {
          user: "Witness",
          existingName: namePopup.witnessObj.name,
          newName: newNameInput.trim(),
        },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwt}` } }
      );
      setFir(response.data);
      updateFir(response.data);
      closeNamePopup();
    } catch {
      setNameError("Failed to update the name. Please try again.");
    } finally {
      setIsSavingName(false);
    }
  };

  const answerTranslation = useAnswerTranslation((path, body) =>
    axios.post(API_URL + path, body, {
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("jwtToken")}` },
    })
  );

  const [answerPopup, setAnswerPopup] = useState(null); // { witnessObj, question }
  const [answerText, setAnswerText] = useState("");
  const [isSavingAnswer, setIsSavingAnswer] = useState(false);
  const [answerError, setAnswerError] = useState(null);

  const openAnswerPopup = (witnessObj, question) => {
    setAnswerPopup({ witnessObj, question });
    setAnswerText("");
    setAnswerError(null);
    answerTranslation.reset();
  };

  const closeAnswerPopup = () => {
    setAnswerPopup(null);
    setAnswerText("");
    setAnswerError(null);
    answerTranslation.reset();
  };

  const saveAnswer = async () => {
    if (!answerPopup || !answerText.trim()) return;

    setIsSavingAnswer(true);
    setAnswerError(null);

    const jwt = localStorage.getItem("jwtToken");
    try {
      const answerTranslationText = await answerTranslation.ensureTranslation(answerText);
      const response = await axios.post(
        API_URL + `/fir/iq/answer/${fir.id}`,
        {
          user: "Witness",
          name: answerPopup.witnessObj.name,
          question: answerPopup.question.question,
          answer: answerText.trim(),
          answerLanguage: answerTranslation.answerLanguage,
          answerTranslationLanguage: answerTranslation.translationLanguage,
          answerTranslation: answerTranslationText,
        },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwt}` } }
      );
      setFir(response.data);
      updateFir(response.data);
      closeAnswerPopup();
    } catch (err) {
      // A failed translation already shows its own message next to the translation box.
      if (err?.message !== "translate failed") setAnswerError("Failed to save the answer. Please try again.");
    } finally {
      setIsSavingAnswer(false);
    }
  };

  const [showAddWitness, setShowAddWitness] = useState(false);
  const [newWitnessName, setNewWitnessName] = useState("");
  const [newWitnessShortIntro, setNewWitnessShortIntro] = useState("");
  const [newWitnessEyewitness, setNewWitnessEyewitness] = useState("");
  const [newWitnessRelevance, setNewWitnessRelevance] = useState("");
  const [newWitnessRelationComplainant, setNewWitnessRelationComplainant] = useState("");
  const [newWitnessRelationVictim, setNewWitnessRelationVictim] = useState("");
  const [newWitnessRelationAccused, setNewWitnessRelationAccused] = useState("");
  const [addWitnessLoading, setAddWitnessLoading] = useState(false);
  const [addWitnessError, setAddWitnessError] = useState(null);

  const closeAddWitness = () => {
    setShowAddWitness(false);
    setNewWitnessName("");
    setNewWitnessShortIntro("");
    setNewWitnessEyewitness("");
    setNewWitnessRelevance("");
    setNewWitnessRelationComplainant("");
    setNewWitnessRelationVictim("");
    setNewWitnessRelationAccused("");
    setAddWitnessError(null);
  };

  const submitNewWitness = async () => {
    setAddWitnessLoading(true);
    setAddWitnessError(null);

    const jwt = localStorage.getItem("jwtToken");
    try {
      const response = await axios.post(
        API_URL + `/fir/witness`,
        {
          firId: fir.id,
          name: newWitnessName.trim(),
          shortIntro: newWitnessShortIntro.trim(),
          eyewitness: newWitnessEyewitness,
          relevance: newWitnessRelevance.trim(),
          // relationWithComplainant: newWitnessRelationComplainant.trim(),
          relationWithVictim: newWitnessRelationVictim.trim(),
          // relationWithAccused: newWitnessRelationAccused.trim(),
        },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwt}` } }
      );
      setFir(response.data);
      updateFir(response.data);
      closeAddWitness();
    } catch {
      setAddWitnessError("Failed to add witness. Please try again.");
    } finally {
      setAddWitnessLoading(false);
    }
  };

  const tabs = ["Top", "High", "Medium", "Low"];
  const [selectedTabByWitness, setSelectedTabByWitness] = useState({});
  const getWitnessKey = (w) => w?.id || w?.name; // use id if available

  const isQuestionCompleted = (witnessKey, question) => {
    const key = `${witnessKey}__${question.question}`;
    return key in questionStatusByKey ? questionStatusByKey[key] : !!question.status;
  };

  const toggleQuestionStatus = async (witnessObj, witnessKey, question) => {
    const key = `${witnessKey}__${question.question}`;
    const prev = isQuestionCompleted(witnessKey, question);
    setQuestionStatusByKey((s) => ({ ...s, [key]: !prev }));

    const jwt = localStorage.getItem("jwtToken");
    try {
      const response = await axios.post(
        API_URL + `/fir/iq/status/${fir.id}`,
        { user: "Witness", name: witnessObj.name, question: question.question, status: !prev ? "completed" : "pending" },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwt}` } }
      );
      if (response.status === 200) {
        setFir(response.data);
        updateFir(response.data);
      }
    } catch {
      setQuestionStatusByKey((s) => ({ ...s, [key]: prev }));
    }
  };

  const computeTabCounts = (sortedQuestions) => {
    const counts = { Top: 0, High: 0, Medium: 0, Low: 0 };
    for (const q of sortedQuestions) {
      counts[q.priorityLabel] = (counts[q.priorityLabel] || 0) + 1;
    }
    return counts;
  };

  const getDefaultTabForCounts = (counts) => {
    for (const t of tabs) {
      if ((counts[t] || 0) > 0) return t;
    }
    return "Top";
  };

  const onClickGenerate = (witnessObj) => {
    setWitness(witnessObj);
    setConfirmRequest(true);
  };

  const onClose = () => {
    setWitness(null);
    setErrorMessage(null);
    setIsLoading(false);
    setConfirmRequest(false);
  };

  const handleConfirm = async () => {
    setIsLoading(true);

    const jwt = localStorage.getItem("jwtToken");
    const request = {
      name: witness.name,
      relationWithComplainant: witness.relationWithComplainant,
      relationWithVictim: witness.relationWithVictim,
    };

    try {
      const response = await axios.post(
        API_URL + `/fir/witnesses/iq/${fir.id}`,
        request,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );
      setFir(response.data);
      updateFir(response.data);
    } catch (error) {
      setErrorMessage(
        "An error occurred while processing the request. Please try again later."
      );
    } finally {
      setIsLoading(false);
      setConfirmRequest(false);
    }
  };

  /* ---------------- Priority helpers ---------------- */

  const getQuestionPriority = (category) => {
    switch (category) {
      case "Level 1":
        return "Top";
      case "Level 2":
        return "High";
      case "Level 3":
        return "Medium";
      default:
        return "Low";
    }
  };

  const priorityOrder = { Top: 1, High: 2, Medium: 3, Low: 4 };

  const sortQuestionsByPriority = (questions) => {
    if (!Array.isArray(questions)) return [];
    return questions
      .map((q) => ({
        ...q,
        priorityLabel: getQuestionPriority(q.category),
      }))
      .sort(
        (a, b) => priorityOrder[a.priorityLabel] - priorityOrder[b.priorityLabel]
      );
  };

  /* ---------------- Copy popup (per witness) ---------------- */

  const [copyPopupOpen, setCopyPopupOpen] = useState(false);
  const [copyForWitnessKey, setCopyForWitnessKey] = useState(null);
  const [copySelected, setCopySelected] = useState({
    Top: true,
    High: true,
    Medium: true,
    Low: true,
  });
  const [copyFeedback, setCopyFeedback] = useState(null);
  const [copyBtnMode, setCopyBtnMode] = useState("copy"); // "copy" | "done"
  const [copyIncludeAnswers, setCopyIncludeAnswers] = useState(true);

  const closeCopyPopup = () => {
    setCopyPopupOpen(false);
    setCopyFeedback(null);
    setCopyBtnMode("copy");
    setCopyForWitnessKey(null);
  };

  const openCopyPopupForWitness = (witnessKey) => {
    setCopySelected({ Top: true, High: true, Medium: true, Low: true });
    setCopyIncludeAnswers(true);
    setCopyFeedback(null);
    setCopyBtnMode("copy");
    setCopyForWitnessKey(witnessKey);
    setCopyPopupOpen(true);
  };

  const getSelectedQuestions = (witnessObj, selectedMap) => {
    const selectedPriorities = tabs.filter((t) => !!selectedMap[t]);

    const qs = Array.isArray(witnessObj?.interrogationQuestions)
      ? witnessObj.interrogationQuestions
      : [];

    const sorted = sortQuestionsByPriority(qs);

    return sorted.filter((q) => selectedPriorities.includes(q.priorityLabel) && q?.question);
  };

  const handleCopyForWitness = async (witnessObj) => {
    try {
      const picked = getSelectedQuestions(witnessObj, copySelected);
      const text = buildQuestionsPlainText(picked, copyIncludeAnswers);
      if (!text.trim()) {
        setCopyFeedback("No questions match the selected categories.");
        return;
      }

      await navigator.clipboard.writeText(text);

      // tick-mark then close after 2 seconds
      setCopyFeedback(null);
      setCopyBtnMode("done");

      window.setTimeout(() => {
        closeCopyPopup();
      }, 2000);
    } catch (e) {
      setCopyFeedback("Failed to copy. Please try again.");
    }
  };

  const getWitnessObjByKey = (witnessKey) => {
    const list = Array.isArray(fir?.witnessList) ? fir.witnessList : [];
    return list.find((w) => getWitnessKey(w) === witnessKey) || null;
  };

  /* ---------------- Download popup (per witness) ---------------- */

  const [downloadPopupOpen, setDownloadPopupOpen] = useState(false);
  const [downloadForWitnessKey, setDownloadForWitnessKey] = useState(null);
  const [downloadSelected, setDownloadSelected] = useState({
    Top: true,
    High: true,
    Medium: true,
    Low: true,
  });
  const [downloadIncludeAnswers, setDownloadIncludeAnswers] = useState(true);
  const [downloadDateWise, setDownloadDateWise] = useState(false);
  const [downloadSelectedDate, setDownloadSelectedDate] = useState(ALL_DATES_OPTION);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  const openDownloadPopupForWitness = (witnessKey) => {
    setDownloadSelected({ Top: true, High: true, Medium: true, Low: true });
    setDownloadIncludeAnswers(true);
    setDownloadDateWise(false);
    setDownloadSelectedDate(ALL_DATES_OPTION);
    setDownloadError(null);
    setDownloadForWitnessKey(witnessKey);
    setDownloadPopupOpen(true);
  };

  const closeDownloadPopup = () => {
    setDownloadPopupOpen(false);
    setDownloadError(null);
    setDownloadForWitnessKey(null);
  };

  const handleDownloadForWitness = async (witnessObj) => {
    const picked = getSelectedQuestions(witnessObj, downloadSelected);

    if (picked.length === 0) {
      setDownloadError("No questions match the selected categories.");
      return;
    }

    const dateWiseActive = downloadIncludeAnswers && downloadDateWise;

    if (dateWiseActive) {
      const uniqueDates = getUniqueAnswerDates(picked);
      if (downloadSelectedDate === ALL_DATES_OPTION && uniqueDates.length === 0) {
        setDownloadError("No dated answers found for the selected categories.");
        return;
      }
      if (downloadSelectedDate !== ALL_DATES_OPTION && !uniqueDates.some((d) => d.label === downloadSelectedDate)) {
        setDownloadError("No answered questions match the selected date.");
        return;
      }
    }

    setIsDownloading(true);
    setDownloadError(null);

    try {
      const blocks = buildQuestionsDocxBlocks({
        heading: `Examination of ${witnessObj.name}`,
        questions: picked,
        includeAnswers: downloadIncludeAnswers,
        dateWise: dateWiseActive,
        selectedDate: downloadSelectedDate,
      });
      const file = await createStructuredDocxFile(blocks, `${witnessObj.name}_Examination_Questions.docx`);
      downloadFile(file);
      closeDownloadPopup();
    } catch (e) {
      setDownloadError("Failed to generate the Word document. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  /* -------------------------------------------------- */

  return (
    <TableSection title="">
      <div className="fir-table-container">
        <div style={{ display: "flex", flexDirection: "column", width: "100%", clear: "both" }}>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "10px" }}>
            <button
              type="button"
              onClick={() => setShowAddWitness(true)}
              style={{ backgroundColor: "#007bff", color: "white", border: "none", borderRadius: "6px", padding: "8px 14px", cursor: "pointer", fontWeight: 600 }}
            >
              + Add Witness
            </button>
          </div>

          <table className="fir-table">
            <thead>
              <tr>
                <th>S. No.</th>
                <th>Witness</th>
                <th>Source</th>
                <th>Interrogation Questions</th>
              </tr>
            </thead>

            <tbody>
              {fir.witnessList &&
                fir.witnessList.map((witnessObj, index) => {
                  const questions = Array.isArray(witnessObj?.interrogationQuestions)
                    ? witnessObj.interrogationQuestions
                    : [];

                  const hasQuestions = questions.length > 0;
                  const sortedQuestions = sortQuestionsByPriority(questions);

                  return (
                    <tr key={index}>
                      {/* Serial Number */}
                      <td
                        style={{
                          verticalAlign: "top",
                          paddingTop: "15px",
                          width: "4%",
                        }}
                      >
                        {index + 1}
                      </td>

                      {/* Witness Details */}
                      <td
                        style={{
                          textAlign: "justify",
                          verticalAlign: "top",
                          paddingTop: "15px",
                          width: "26%",
                        }}
                      >
                        <div>
                          <strong>Name:</strong> {witnessObj.name}{" "}
                          <button
                            type="button"
                            onClick={() => openNamePopup(witnessObj)}
                            title="Edit name"
                            aria-label="Edit name"
                            style={{ background: "none", border: "none", padding: 0, cursor: "pointer", verticalAlign: "middle" }}
                          >
                            <FaPencilAlt size={13} color="#6b7280" />
                          </button>
                        </div>
                        <br />
                        <div>
                          <strong>Relation with Victim:</strong>{" "}
                          {witnessObj.relationWithVictim || "-"}
                        </div>
                        <br />
                        <div>
                          <strong>Bio:</strong> {witnessObj.shortIntro || "-"}
                        </div>
                        <br />
                        <div>
                          <strong>Eyewitness:</strong>{" "}
                          {witnessObj.eyewitness || "-"}
                        </div>
                        <br />
                        <div>
                          <strong>Relevance:</strong>{" "}
                          {witnessObj.relevance || "-"}
                        </div>
                      </td>

                      {/* Source */}
                      <td
                        style={{
                          textAlign: "justify",
                          verticalAlign: "top",
                          paddingTop: "15px",
                          width: "20%",
                        }}
                      >
                        <div>{witnessObj.source || "FIR"}</div>
                      </td>

                      {/* Interrogation Questions */}
                      <td
                        style={{
                          textAlign: "justify",
                          verticalAlign: "top",
                          paddingTop: "15px",
                          width: "70%",
                        }}
                      >
                        {hasQuestions ? (
                          (() => {
                            const witnessKey = getWitnessKey(witnessObj);
                            const tabCounts = computeTabCounts(sortedQuestions);

                            const selectedTab =
                              selectedTabByWitness[witnessKey] ??
                              getDefaultTabForCounts(tabCounts);

                            const filteredQuestions = sortedQuestions.filter(
                              (q) => q.priorityLabel === selectedTab
                            );

                            return (
                              <>
                                {/* First row inside cell: Tabs left, Copy right */}
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "10px",
                                    marginBottom: "12px",
                                    width: "100%",
                                    clear: "both",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  {/* Tabs */}
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: "10px",
                                      flexWrap: "wrap",
                                      alignItems: "center",
                                    }}
                                  >
                                    {tabs.map((tab) => {
                                      const isActive = tab === selectedTab;
                                      const count = tabCounts[tab] ?? 0;

                                      return (
                                        <button
                                          key={tab}
                                          type="button"
                                          onClick={() =>
                                            setSelectedTabByWitness((prev) => ({
                                              ...prev,
                                              [witnessKey]: tab,
                                            }))
                                          }
                                          style={{
                                            padding: "6px 10px",
                                            borderRadius: "999px",
                                            border: isActive
                                              ? "1px solid #2563eb"
                                              : "1px solid #d1d5db",
                                            background: isActive ? "#2563eb" : "#ffffff",
                                            color: isActive ? "#ffffff" : "#111827",
                                            fontWeight: isActive ? 700 : 500,
                                            cursor: "pointer",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            fontSize: "13px",
                                          }}
                                        >
                                          <span>{tab}</span>

                                          <span
                                            style={{
                                              minWidth: "20px",
                                              height: "20px",
                                              padding: "0 6px",
                                              borderRadius: "999px",
                                              display: "inline-flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              fontSize: "11px",
                                              fontWeight: 800,
                                              lineHeight: 1,
                                              background: isActive ? "#ffffff" : "grey",
                                              color: isActive ? "#2563eb" : "#ffffff",
                                            }}
                                          >
                                            {count}
                                          </span>
                                        </button>
                                      );
                                    })}
                                  </div>

                                  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                    {/* Copy button per witness */}
                                    <button
                                      type="button"
                                      onClick={() => openCopyPopupForWitness(witnessKey)}
                                      title="Copy questions for this witness"
                                      aria-label="Copy questions for this witness"
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        border: "1px solid #d1d5db",
                                        borderRadius: "8px",
                                        padding: "6px 10px",
                                        background: "#ffffff",
                                        cursor: "pointer",
                                        fontSize: "13px",
                                        fontWeight: 600,
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        aria-hidden="true"
                                      >
                                        <path
                                          d="M8 8V6.6C8 5.71634 8.71634 5 9.6 5H18.4C19.2837 5 20 5.71634 20 6.6V15.4C20 16.2837 19.2837 17 18.4 17H17"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                        />
                                        <path
                                          d="M4 8.6C4 7.71634 4.71634 7 5.6 7H14.4C15.2837 7 16 7.71634 16 8.6V19.4C16 20.2837 15.2837 21 14.4 21H5.6C4.71634 21 4 20.2837 4 19.4V8.6Z"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                        />
                                      </svg>
                                      <span>Copy</span>
                                    </button>

                                    {/* Download button per witness */}
                                    <button
                                      type="button"
                                      onClick={() => openDownloadPopupForWitness(witnessKey)}
                                      title="Download questions for this witness"
                                      aria-label="Download questions for this witness"
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        border: "1px solid #d1d5db",
                                        borderRadius: "8px",
                                        padding: "6px 10px",
                                        background: "#ffffff",
                                        cursor: "pointer",
                                        fontSize: "13px",
                                        fontWeight: 600,
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      <FaFileWord style={{ fontSize: "16px", color: "#2B579A" }} />
                                      <span>Download</span>
                                    </button>
                                  </div>
                                </div>

                                {/* Questions filtered by selected tab */}
                                {filteredQuestions.length === 0 ? (
                                  <div
                                    style={{
                                      color: "grey",
                                      fontSize: "14px",
                                      marginBottom: "12px",
                                    }}
                                  >
                                    No questions in <b>{selectedTab}</b>.
                                  </div>
                                ) : (
                                  filteredQuestions.map((q, i) => {
                                    const isCompleted = isQuestionCompleted(witnessKey, q);
                                    return (
                                      <div key={i} style={{ marginBottom: "15px" }}>
                                        <div>
                                          <strong>Question {i + 1}:</strong> {q.question}
                                        </div>

                                        <div
                                          style={{
                                            color: "grey",
                                            marginTop: "5px",
                                            fontSize: "15px",
                                          }}
                                        >
                                          {q.relevance}
                                        </div>

                                        {Array.isArray(q.answer) && q.answer.length > 0 && (
                                          <div style={{ marginTop: "5px", fontSize: "15px" }}>
                                            <strong>Answer:</strong>
                                            {q.answer.map((ans, idx) => (
                                              <div key={idx} style={{ marginTop: "4px" }}>
                                                {idx + 1}. {ans}
                                                {getAnswerTranslation(q, idx) && (
                                                  <div style={{ marginTop: "2px" }}>
                                                    <strong>Translation:</strong> {getAnswerTranslation(q, idx)}
                                                  </div>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        )}

                                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "12px", flexWrap: "wrap" }}>
                                          {!(Array.isArray(q.answer) && q.answer.length > 0) && (
                                            <button
                                              type="button"
                                              onClick={() => openAnswerPopup(witnessObj, q)}
                                              style={{
                                                border: "1px solid #367aef",
                                                borderRadius: "8px",
                                                padding: "4px 10px",
                                                marginRight: "20px",
                                                background: "#ffffff",
                                                cursor: "pointer",
                                                fontSize: "13px",
                                                fontWeight: 600,
                                                color: "#111827",
                                              }}
                                            >
                                              Record Answer
                                            </button>
                                          )}
                                          <ToggleSwitch
                                            checked={isCompleted}
                                            onChange={() => toggleQuestionStatus(witnessObj, witnessKey, q)}
                                          />
                                          <span style={{ fontSize: "13px", color: isCompleted ? "#4CAF50" : "#E53935" }}>
                                            {isCompleted ? "Completed" : "Pending"}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })
                                )}

                                {/* Regenerate Questions Button (unchanged) */}
                                {localStorage.getItem("role") === "admin" && (
                                  <button
                                    style={{
                                      backgroundColor: "transparent",
                                      color: "#007BFF",
                                      border: "none",
                                      borderBottom: "1px solid #007BFF",
                                      padding: "4px 0 2px 0",
                                      cursor: "pointer",
                                      fontSize: "15px",
                                      fontWeight: "500",
                                      lineHeight: "1.2",
                                      transition: "all 0.3s ease",
                                    }}
                                    disabled={isLoading || confirmRequest}
                                    onClick={() => onClickGenerate(witnessObj)}
                                    onMouseOver={(e) => {
                                      e.currentTarget.style.color = "#0056b3";
                                      e.currentTarget.style.borderBottom =
                                        "1px solid #0056b3";
                                    }}
                                    onMouseOut={(e) => {
                                      e.currentTarget.style.color = "#007BFF";
                                      e.currentTarget.style.borderBottom =
                                        "1px solid #007BFF";
                                    }}
                                  >
                                    Regenerate Questions
                                  </button>
                                )}
                              </>
                            );
                          })()
                        ) : (
                          <button
                            style={{
                              backgroundColor: "#007bff",
                              color: "white",
                              border: "none",
                              borderRadius: "6px",
                              padding: "8px 14px",
                              cursor: "pointer",
                            }}
                            disabled={isLoading || confirmRequest}
                            onClick={() => onClickGenerate(witnessObj)}
                          >
                            Generate Questions
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Copy popup (per witness) */}
      {copyPopupOpen && (
        <div className="popup-overlay">
          <div className="popup-container" style={{ maxWidth: "520px", width: "92%" }}>
            <h3>Copy Interrogation Questions</h3>

            {/* Checkboxes centered in one row */}
            <div
              style={{
                display: "flex",
                gap: "16px",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                flexWrap: "nowrap",
                marginTop: "14px",
                marginBottom: "18px",
              }}
            >
              {tabs.map((label) => (
                <label
                  key={label}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    userSelect: "none",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!copySelected[label]}
                    onChange={(e) =>
                      setCopySelected((prev) => ({
                        ...prev,
                        [label]: e.target.checked,
                      }))
                    }
                    style={{ width: "14px", height: "14px", margin: 0 }}
                  />
                  {label}
                </label>
              ))}
            </div>

            {hasAnyAnswers(getWitnessObjByKey(copyForWitnessKey)?.interrogationQuestions) && (
              <div style={{ display: "flex", justifyContent: "center", marginBottom: "14px" }}>
                <label style={{ display: "inline-flex", alignItems: "center", gap: "8px", userSelect: "none", cursor: "pointer", fontSize: "14px", fontWeight: 600, whiteSpace: "nowrap" }}>
                  <input
                    type="checkbox"
                    checked={copyIncludeAnswers}
                    onChange={(e) => setCopyIncludeAnswers(e.target.checked)}
                    style={{ width: "14px", height: "14px", margin: 0 }}
                  />
                  Include Answers
                </label>
              </div>
            )}

            {copyFeedback && (
              <div style={{ color: "grey", fontSize: "14px", marginBottom: 10 }}>
                {copyFeedback}
              </div>
            )}

            <div className="popup-buttons">
              <button onClick={closeCopyPopup} className="cancel-button" disabled={copyBtnMode === "done"} >
                Cancel
              </button>

              <button
                onClick={() => {
                  const wObj = getWitnessObjByKey(copyForWitnessKey);
                  handleCopyForWitness(wObj);
                }}
                className="submit-popup-button"
                disabled={copyBtnMode === "done"}
              >
                {copyBtnMode === "done" ? (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      fontWeight: 700,
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M20 6L9 17L4 12"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Done
                  </span>
                ) : (
                  "Copy"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {downloadPopupOpen && (
        <div className="popup-overlay">
          <div className="popup-container" style={{ maxWidth: "520px", width: "92%" }}>
            <h3>Download Examination Questions</h3>

            <div
              style={{
                display: "flex",
                gap: "16px",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                flexWrap: "nowrap",
                marginTop: "14px",
                marginBottom: "18px",
              }}
            >
              {tabs.map((label) => (
                <label
                  key={label}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    userSelect: "none",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!downloadSelected[label]}
                    onChange={(e) =>
                      setDownloadSelected((prev) => ({
                        ...prev,
                        [label]: e.target.checked,
                      }))
                    }
                    style={{ width: "14px", height: "14px", margin: 0 }}
                  />
                  {label}
                </label>
              ))}
            </div>

            {(() => {
              const witnessObjForPopup = getWitnessObjByKey(downloadForWitnessKey);
              if (!hasAnyAnswers(witnessObjForPopup?.interrogationQuestions)) return null;

              const questionsForPopup = getSelectedQuestions(witnessObjForPopup, downloadSelected);
              const uniqueDates = getUniqueAnswerDates(questionsForPopup);

              return (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "16px", flexWrap: "wrap", marginBottom: "14px" }}>
                  <label style={{ display: "inline-flex", alignItems: "center", gap: "8px", userSelect: "none", cursor: "pointer", fontSize: "14px", fontWeight: 600, whiteSpace: "nowrap" }}>
                    <input
                      type="checkbox"
                      checked={downloadIncludeAnswers}
                      onChange={(e) => setDownloadIncludeAnswers(e.target.checked)}
                      style={{ width: "14px", height: "14px", margin: 0 }}
                    />
                    Include Answers
                  </label>

                  <label
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      userSelect: "none",
                      cursor: downloadIncludeAnswers ? "pointer" : "not-allowed",
                      fontSize: "14px",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      opacity: downloadIncludeAnswers ? 1 : 0.5,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={downloadDateWise}
                      disabled={!downloadIncludeAnswers}
                      onChange={(e) => setDownloadDateWise(e.target.checked)}
                      style={{ width: "14px", height: "14px", margin: 0 }}
                    />
                    Date Wise
                  </label>

                  <select
                    value={downloadSelectedDate}
                    disabled={!downloadIncludeAnswers || !downloadDateWise}
                    onChange={(e) => setDownloadSelectedDate(e.target.value)}
                    style={{ fontSize: "14px", padding: "4px 6px", position: "relative", top: "-4px", opacity: downloadIncludeAnswers && downloadDateWise ? 1 : 0.5 }}
                  >
                    <option value={ALL_DATES_OPTION}>{ALL_DATES_OPTION}</option>
                    {uniqueDates.map((d) => (
                      <option key={d.raw} value={d.label}>{d.label}</option>
                    ))}
                  </select>
                </div>
              );
            })()}

            {downloadError && (
              <div style={{ color: "#c9302c", fontSize: "14px", marginBottom: 10 }}>
                {downloadError}
              </div>
            )}

            <div className="popup-buttons">
              <button onClick={closeDownloadPopup} className="cancel-button" disabled={isDownloading}>
                Cancel
              </button>

              <button
                onClick={() => {
                  const wObj = getWitnessObjByKey(downloadForWitnessKey);
                  handleDownloadForWitness(wObj);
                }}
                disabled={isDownloading}
                className="submit-popup-button"
              >
                {isDownloading ? <div className="popup-btn-spinner"></div> : "Download"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error popup */}
      {errorMessage && (
        <div className="popup-overlay">
          <div
            className="error-popup"
            style={{
              border: "3px solid white",
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "8px",
              textAlign: "center",
              maxWidth: "400px",
              margin: "20px auto",
            }}
          >
            <div
              className="error-message"
              style={{
                marginBottom: "15px",
                fontSize: "16px",
                color: "#333",
              }}
            >
              {errorMessage}
            </div>
            <button
              className="error-ok-button"
              onClick={() => setErrorMessage(null)}
              style={{
                padding: "10px 20px",
                fontSize: "14px",
                color: "white",
                backgroundColor: "#007bff",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Confirm popup */}
      {confirmRequest && (
        <div className="popup-overlay">
          <div className="popup-container">
            <h3>{(SECTION_INFO_I18N[getPreferredLang()] || SECTION_INFO_I18N.en).examinations.title}</h3>
            <p style={{ textAlign: "justify" }}>
              {(SECTION_INFO_I18N[getPreferredLang()] || SECTION_INFO_I18N.en).examinations.text}
              <br /><br />
              {CONTINUE_PROMPT_I18N[getPreferredLang()] || CONTINUE_PROMPT_I18N.en}
            </p>
            <div className="popup-buttons">
              <button
                onClick={onClose}
                className="cancel-button"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                className="submit-popup-button"
                disabled={isLoading}
              >
                <div
                  style={{
                    minHeight: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {isLoading ? <div className="spinner"></div> : "Confirm"}
                </div>
              </button>
            </div>
            {isLoading && (
              <div
                style={{
                  textAlign: "center",
                  marginTop: "20px",
                  fontWeight: "bold",
                }}
              >
                Please wait. It may take about a minute to do the analysis.
              </div>
            )}
          </div>
        </div>
      )}

      {showAddWitness && (
        <div className="popup-overlay">
          <div className="popup-container" style={{ maxWidth: "520px", width: "92%" }}>
            <h3>Add Witness</h3>

            <div style={{ marginBottom: "12px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
                <div style={{ fontWeight: 600, marginBottom: "8px" }}>Eyewitness</div>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={newWitnessEyewitness === "Yes"}
                    onChange={() => setNewWitnessEyewitness(newWitnessEyewitness === "Yes" ? "" : "Yes")}
                    style={{ width: "16px", height: "16px", margin: 0 }}
                  />
                  Yes
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={newWitnessEyewitness === "No"}
                    onChange={() => setNewWitnessEyewitness(newWitnessEyewitness === "No" ? "" : "No")}
                    style={{ width: "16px", height: "16px", margin: 0 }}
                  />
                  No
                </label>
              </div>
            </div>

            <input
              placeholder="Witness Name"
              value={newWitnessName}
              onChange={(e) => setNewWitnessName(e.target.value)}
              style={{ width: "100%", boxSizing: "border-box", borderRadius: "6px", border: "1px solid #ccc", padding: "10px", marginBottom: "12px" }}
            />

            <textarea
              placeholder="Bio (Brief Intro)"
              value={newWitnessShortIntro}
              onChange={(e) => setNewWitnessShortIntro(e.target.value)}
              rows={2}
              style={{ width: "100%", boxSizing: "border-box", borderRadius: "6px", border: "1px solid #ccc", padding: "10px", marginBottom: "12px", fontFamily: "inherit" }}
            />



            <textarea
              placeholder="Relevance"
              value={newWitnessRelevance}
              onChange={(e) => setNewWitnessRelevance(e.target.value)}
              rows={2}
              style={{ width: "100%", boxSizing: "border-box", borderRadius: "6px", border: "1px solid #ccc", padding: "10px", marginBottom: "12px", fontFamily: "inherit" }}
            />

            {/* <textarea
              placeholder="Relation with Complainant (optional)"
              value={newWitnessRelationComplainant}
              onChange={(e) => setNewWitnessRelationComplainant(e.target.value)}
              rows={2}
              style={{ width: "100%", boxSizing: "border-box", borderRadius: "6px", border: "1px solid #ccc", padding: "10px", marginBottom: "12px", fontFamily: "inherit" }}
            /> */}

            <textarea
              placeholder="Relation with Victim (optional)"
              value={newWitnessRelationVictim}
              onChange={(e) => setNewWitnessRelationVictim(e.target.value)}
              rows={2}
              style={{ width: "100%", boxSizing: "border-box", borderRadius: "6px", border: "1px solid #ccc", padding: "10px", marginBottom: "12px", fontFamily: "inherit" }}
            />
            {/* 
            <textarea
              placeholder="Relation with Accused (optional)"
              value={newWitnessRelationAccused}
              onChange={(e) => setNewWitnessRelationAccused(e.target.value)}
              rows={2}
              style={{ width: "100%", boxSizing: "border-box", borderRadius: "6px", border: "1px solid #ccc", padding: "10px", marginBottom: "12px", fontFamily: "inherit" }}
            /> */}

            {addWitnessError && <p style={{ color: "#c9302c" }}>{addWitnessError}</p>}

            <div className="popup-buttons">
              <button onClick={closeAddWitness} className="cancel-button" disabled={addWitnessLoading}>Cancel</button>
              <button
                onClick={submitNewWitness}
                className="submit-popup-button"
                disabled={addWitnessLoading || !newWitnessName.trim() || !newWitnessShortIntro.trim() || !newWitnessEyewitness || !newWitnessRelevance.trim()}
              >
                {addWitnessLoading ? <div className="popup-btn-spinner"></div> : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}

      {answerPopup && (
        <div className="popup-overlay">
          <div className="popup-container" style={{ maxWidth: "520px", width: "92%" }}>
            <h3>Record Answer</h3>
            <p style={{ fontWeight: 600, marginBottom: "10px" }}>{answerPopup.question.question}</p>
            <AnswerLanguageSelect t={answerTranslation} disabled={isSavingAnswer} />
            <textarea
              value={answerText}
              onChange={(e) => setAnswerText(e.target.value)}
              rows={6}
              placeholder="Type the answer here."
              style={{ width: "100%", boxSizing: "border-box", borderRadius: "6px", border: "1px solid #ccc", padding: "10px", fontSize: "15px", fontFamily: "inherit", resize: "vertical" }}
            />
            <TranslatedAnswerBox t={answerTranslation} answerText={answerText} disabled={isSavingAnswer} />
            {answerError && <p style={{ color: "#c9302c" }}>{answerError}</p>}
            <div className="popup-buttons">
              <button onClick={closeAnswerPopup} className="cancel-button" disabled={isSavingAnswer}>Cancel</button>
              <button
                onClick={saveAnswer}
                className="submit-popup-button"
                disabled={isSavingAnswer || answerTranslation.isTranslating || !answerText.trim()}
              >
                {isSavingAnswer ? <div className="popup-btn-spinner"></div> : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {namePopup && (
        <div className="popup-overlay">
          <div className="popup-container" style={{ maxWidth: "420px", width: "92%" }}>
            <h3>Update Name</h3>
            <input
              type="text"
              value={newNameInput}
              onChange={(e) => setNewNameInput(e.target.value)}
              placeholder="Enter the updated name"
              style={{ width: "100%", boxSizing: "border-box", borderRadius: "6px", border: "1px solid #ccc", padding: "10px", fontSize: "15px", marginTop: "10px" }}
            />
            {nameError && <p style={{ color: "#c9302c", marginTop: "8px" }}>{nameError}</p>}
            <div className="popup-buttons">
              <button onClick={closeNamePopup} className="cancel-button" disabled={isSavingName}>Cancel</button>
              <button
                onClick={saveName}
                className="submit-popup-button"
                disabled={isSavingName || !newNameInput.trim()}
              >
                {isSavingName ? <div className="popup-btn-spinner"></div> : "Update"}
              </button>
            </div>
          </div>
        </div>
      )}
    </TableSection>
  );
};

const StatementTable = ({ API_URL, fir: defaultFIR, updateFir }) => {
  const [fir, setFir] = useState(defaultFIR);
  const [statementText, setStatementText] = useState("");
  const [confirmSummaryRequest, setConfirmSummaryRequest] = useState(false);
  const [confirmEventsRequest, setConfirmEventsRequest] = useState(false);
  const [confirmEvidenceRequest, setConfirmEvidenceRequest] = useState(false);
  const [confirmWitnessRequest, setConfirmWitnessRequest] = useState(false);
  const [confirmAccusedRequest, setConfirmAccusedRequest] = useState(false);
  const [confirmFollowupRequest, setConfirmFollowupRequest] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingFIR, setIsLoadingFIR] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const [statement, setStatement] = useState(null);

  const onClickSummarize = (statement) => {
    setStatement(statement);
    setConfirmSummaryRequest(true);
  }

  const onClickExtractEvents = (statement) => {
    setStatement(statement);
    setConfirmEventsRequest(true);
  }

  const onClickIdentifyEvidences = (statement) => {
    setStatement(statement);
    setConfirmEvidenceRequest(true);
  }

  const onClickIdentifyWitnesses = (statement) => {
    setStatement(statement);
    setConfirmWitnessRequest(true);
  }

  const onClickIdentifyAccused = (statement) => {
    setStatement(statement);
    setConfirmAccusedRequest(true);
  }

  const onClickIdentifyFollowupQuestions = (statement) => {
    setStatement(statement);
    setConfirmFollowupRequest(true);
  }

  const onClose = () => {
    setStatement(null);
    setErrorMessage(null);
    setIsLoading(false);
    setConfirmSummaryRequest(false);
    setConfirmEventsRequest(false);
    setConfirmEvidenceRequest(false);
    setConfirmWitnessRequest(false);
    setConfirmAccusedRequest(false);
    setConfirmFollowupRequest(false);
  };

  const handleConfirmSummary = async () => {
    setIsLoading(true);

    var jwt = localStorage.getItem('jwtToken');

    const formData = new FormData();
    formData.append('id', fir.id);
    formData.append('respondent', statement.respondent);
    formData.append('interrogationDate', statement.interrogationDate);

    try {
      const response = await axios.post(API_URL + `/fir/statement/summary`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${jwt}`,
        },
      });
      //console.log(response.data);
      setFir(response.data);
      updateFir(response.data);
    } catch (error) {
      if (error.status === 401) {
        setErrorMessage("Your session has expired. Please login again to continue.");
      }
      else {
        setErrorMessage("An error occurred while processing the request. Please try again later.");
      }
    } finally {
      setIsLoading(false);
      setConfirmSummaryRequest(false);
    }
  };

  const handleConfirmEvents = async () => {
    setIsLoading(true);

    var jwt = localStorage.getItem('jwtToken');

    const formData = new FormData();
    formData.append('id', fir.id);
    formData.append('respondent', statement.respondent);
    formData.append('interrogationDate', statement.interrogationDate);

    try {
      const response = await axios.post(API_URL + `/fir/statement/events`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${jwt}`,
        },
      });
      //console.log(response.data);
      setFir(response.data);
      updateFir(response.data);
    } catch (error) {
      if (error.status === 401) {
        setErrorMessage("Your session has expired. Please login again to continue.");
      }
      else {
        setErrorMessage("An error occurred while processing the request. Please try again later.");
      }
    } finally {
      setIsLoading(false);
      setConfirmEventsRequest(false);
    }
  };

  const handleConfirmEvidences = async () => {
    setIsLoading(true);

    var jwt = localStorage.getItem('jwtToken');

    const formData = new FormData();
    formData.append('id', fir.id);
    formData.append('respondent', statement.respondent);
    formData.append('interrogationDate', statement.interrogationDate);

    try {
      const response = await axios.post(API_URL + `/fir/statement/evidences`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${jwt}`,
        },
      });
      console.log(response.data);
      if (response.data) {
        setFir(response.data);
        updateFir(response.data);
      }
    } catch (error) {
      if (error.status === 401) {
        setErrorMessage("Your session has expired. Please login again to continue.");
      }
      else {
        setErrorMessage("An error occurred while processing the request. Please try again later.");
      }
    } finally {
      setIsLoading(false);
      setConfirmEvidenceRequest(false);
    }
  };

  const handleConfirmWitnesses = async () => {
    setIsLoading(true);

    var jwt = localStorage.getItem('jwtToken');

    const formData = new FormData();
    formData.append('id', fir.id);
    formData.append('respondent', statement.respondent);
    formData.append('interrogationDate', statement.interrogationDate);

    try {
      const response = await axios.post(API_URL + `/fir/statement/witnesses`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${jwt}`,
        },
      });
      //console.log(response.data);
      setFir(response.data);
      updateFir(response.data);
    } catch (error) {
      if (error.status === 401) {
        setErrorMessage("Your session has expired. Please login again to continue.");
      }
      else {
        setErrorMessage("An error occurred while processing the request. Please try again later.");
      }
    } finally {
      setIsLoading(false);
      setConfirmWitnessRequest(false);
    }
  };

  const handleConfirmAccused = async () => {
    setIsLoading(true);

    var jwt = localStorage.getItem('jwtToken');

    const formData = new FormData();
    formData.append('id', fir.id);
    formData.append('respondent', statement.respondent);
    formData.append('interrogationDate', statement.interrogationDate);

    try {
      const response = await axios.post(API_URL + `/fir/statement/accused`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${jwt}`,
        },
      });
      //console.log(response.data);
      setFir(response.data);
      updateFir(response.data);
    } catch (error) {
      if (error.status === 401) {
        setErrorMessage("Your session has expired. Please login again to continue.");
      }
      else {
        setErrorMessage("An error occurred while processing the request. Please try again later.");
      }
    } finally {
      setIsLoading(false);
      setConfirmAccusedRequest(false);
    }
  };

  const handleConfirmFollowup = async () => {
    setIsLoading(true);

    var jwt = localStorage.getItem('jwtToken');

    const formData = new FormData();
    formData.append('id', fir.id);
    formData.append('respondent', statement.respondent);
    formData.append('interrogationDate', statement.interrogationDate);

    try {
      const response = await axios.post(API_URL + `/fir/statement/iq`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${jwt}`,
        },
      });
      //console.log(response.data);
      setFir(response.data);
      updateFir(response.data);
    } catch (error) {
      if (error.status === 401) {
        setErrorMessage("Your session has expired. Please login again to continue.");
      }
      else {
        setErrorMessage("An error occurred while processing the request. Please try again later.");
      }
    } finally {
      setIsLoading(false);
      setConfirmFollowupRequest(false);
    }
  };

  const handleRefresh = async () => {
    const jwtToken = localStorage.getItem("jwtToken");
    if (!jwtToken) {
      setErrorMessage("Session Expired. Please log in again.");
      return;
    }

    setIsLoadingFIR(true);
    try {
      const response = await axios.get(`${API_URL}/fir/${fir.id}`, {
        headers: { Authorization: `Bearer ${jwtToken}` },
      });

      if (response.status === 200) {
        setFir(response.data);
        updateFir(response.data);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        setErrorMessage("Your session has expired. Please login again to continue.");
      } else {
        setErrorMessage("An error occurred while refreshing. Please try again later.");
      }
    } finally {
      setIsLoadingFIR(false);
    }
  };

  const renderProcessingMessage = () => (
    <span>
      AI assistant is processing this section. Click{" "}
      <span
        onClick={handleRefresh}
        style={{
          color: "#007bff",
          cursor: "pointer",
          textDecoration: "underline",
          fontWeight: "bold",
        }}
        onMouseOver={(e) => (e.currentTarget.style.color = "#0056b3")}
        onMouseOut={(e) => (e.currentTarget.style.color = "#007bff")}
      >
        Refresh
      </span>{" "}
      in about 30 minutes to view results.
    </span>
  );

  return (
    <TableSection title="">
      <div className="fir-table-container">
        <table className="fir-table">
          <tbody>
            {fir?.investigation?.responses?.map((statement, index) => {
              const hasEvents =
                statement?.eventsAndTimeline &&
                Object.keys(statement.eventsAndTimeline).length > 0;

              const hasEvidences =
                statement?.evidenceList &&
                Object.keys(statement.evidenceList).length > 0;

              const hasWitnesses =
                statement?.witnessList &&
                statement.witnessList.length > 0;

              const hasAccused =
                statement?.accusedList &&
                statement.accusedList.length > 0;

              return (
                <React.Fragment key={index}>
                  {/* ================= STATEMENT MARKER ================= */}
                  <tr className="statement-marker">
                    <td colSpan={2} style={{
                      fontWeight: "bold",
                      background: "#f5f5f5",
                      padding: "10px",
                      borderTop: "2px solid #ccc"
                    }}>
                      Statement {index + 1}
                    </td>
                  </tr>

                  {/* ================= WITNESS ================= */}
                  <tr>
                    <td className="label-col">Testifier</td>
                    <td className="value-col">
                      <div style={{ marginBottom: "6px" }}>
                        {statement.respondent} ({statement.gender}, {statement.age})
                      </div>
                      {statement.interrogationDate && (
                        <div style={{ marginBottom: "6px" }}>Interrogation Date: {statement.interrogationDate}</div>
                      )}
                      <div><strong>Eyewitness:</strong> {statement.ofEyewitness}</div>

                      <button
                        style={{
                          backgroundColor: "transparent",
                          color: "#007BFF",
                          border: "none",
                          borderBottom: "1px solid #007BFF",
                          padding: "4px 0 2px 0",
                          cursor: "pointer",
                          fontSize: "15px",
                          fontWeight: "500",
                          lineHeight: "1.2",
                          transition: "all 0.3s ease",
                          marginTop: "8px"
                        }}
                        disabled={isLoading}
                        onClick={() => setStatementText(statement.text)}
                        onMouseOver={(e) => {
                          e.currentTarget.style.color = "#0056b3";
                          e.currentTarget.style.borderBottom = "1px solid #0056b3";
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.color = "#007BFF";
                          e.currentTarget.style.borderBottom = "1px solid #007BFF";
                        }}
                      > View Statement </button>
                    </td>
                  </tr>

                  {/* ================= REMARKS ================= */}
                  <tr>
                    <td className="label-col">Remarks</td>
                    <td className="value-col">
                      <div style={{ textAlign: "justify", lineHeight: "1.6", whiteSpace: "pre-wrap" }}>
                        {statement.statementSummary || renderProcessingMessage()}
                      </div>

                      {localStorage.getItem('role') === 'admin' && (
                        <button
                          style={{
                            backgroundColor: "transparent",
                            color: "#007BFF",                   // blue (Bootstrap-style)
                            border: "none",
                            borderBottom: "1px solid #007BFF",  // line matches text color
                            padding: "4px 0 2px 0",
                            cursor: "pointer",
                            fontSize: "15px",
                            fontWeight: "500",
                            lineHeight: "1.2",
                            transition: "all 0.3s ease",
                            marginTop: "8px"
                          }}
                          disabled={isLoading || confirmSummaryRequest}
                          onClick={() => onClickSummarize(statement)}
                          onMouseOver={(e) => {
                            e.currentTarget.style.color = "#0056b3"; // darker blue
                            e.currentTarget.style.borderBottom = "1px solid #0056b3";
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.color = "#007BFF"; // restore original blue
                            e.currentTarget.style.borderBottom = "1px solid #007BFF";
                          }}
                        >
                          Regenerate Remarks
                        </button>
                      )}
                    </td>
                  </tr>

                  {/* ================= Discrepancies ================= */}
                  <tr>
                    <td className="label-col">Discrepancies</td>
                    <td className="value-col">
                      <div style={{ textAlign: "justify", lineHeight: "1.6", whiteSpace: "pre-wrap" }}>
                        {statement.inconsistentWithFIR || renderProcessingMessage()}
                      </div>
                    </td>
                  </tr>

                  {/* ================= EVENTS ================= */}
                  <tr>
                    <td className="label-col">Key Facts</td>
                    <td className="value-col">
                      {hasEvents ? (
                        <>
                          {statement.eventsAndTimeline.map((e, i) => (
                            <div key={i} style={{ marginBottom: 15 }}>
                              <strong>{i + 1}.</strong> {e.description}
                              <div className="muted" style={{ marginTop: 5, marginBottom: 5 }}>
                                <strong>Date:</strong> {e.date || "-"}, <strong>Time:</strong> {e.time || "-"}, <strong>Location:</strong> {e.location || "-"}
                              </div>
                              <div className="muted" style={{ marginTop: 5, marginBottom: 10 }}>
                                <strong>Persons:</strong> {e.personsInvolved ? e.personsInvolved.join(', ') : '-'}
                              </div>
                            </div>
                          ))}
                          {localStorage.getItem('role') === 'admin' && (
                            <button
                              style={{
                                backgroundColor: "transparent",
                                color: "#007BFF",                   // blue (Bootstrap-style)
                                border: "none",
                                borderBottom: "1px solid #007BFF",  // line matches text color
                                padding: "4px 0 2px 0",
                                cursor: "pointer",
                                fontSize: "15px",
                                fontWeight: "500",
                                lineHeight: "1.2",
                                transition: "all 0.3s ease",
                              }}
                              disabled={isLoading || confirmEventsRequest}
                              onClick={() => onClickExtractEvents(statement)}
                            >
                              Re-extract Events
                            </button>
                          )}
                        </>
                      ) : (
                        <div style={{ textAlign: "justify", lineHeight: "1.6", whiteSpace: "pre-wrap" }}>
                          {renderProcessingMessage()}
                          <br />
                          {localStorage.getItem('role') === 'admin' && (
                            <button
                              style={{
                                backgroundColor: "#007bff",
                                color: "white",
                                border: "none",
                                borderRadius: "6px",
                                padding: "8px 14px",
                                cursor: "pointer",
                              }}
                              disabled={isLoading || confirmEventsRequest}
                              onClick={() => onClickExtractEvents(statement)}
                            >
                              Extract Events
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>

                  {/* ================= EVIDENCES ================= */}
                  <tr>
                    <td className="label-col">Evidences</td>
                    <td className="value-col">
                      {hasEvidences ? (
                        <>
                          {Object.entries(statement.evidenceList).map(([key, value], i) => (
                            <div key={i} style={{ marginBottom: "15px" }}>
                              <div>
                                <strong>{i + 1}:</strong> {key}
                              </div>

                              <div style={{ color: "grey", marginTop: "5px", fontSize: "15px" }}>
                                Relevance: {value || "-"}
                              </div>
                            </div>
                          ))}
                          {localStorage.getItem('role') === 'admin' && (
                            <button
                              style={{
                                backgroundColor: "transparent",
                                color: "#007BFF",                   // blue (Bootstrap-style)
                                border: "none",
                                borderBottom: "1px solid #007BFF",  // line matches text color
                                padding: "4px 0 2px 0",
                                cursor: "pointer",
                                fontSize: "15px",
                                fontWeight: "500",
                                lineHeight: "1.2",
                                transition: "all 0.3s ease",
                              }}
                              disabled={isLoading || confirmEvidenceRequest}
                              onClick={() => onClickIdentifyEvidences(statement)}
                            >
                              Re-identify Evidence
                            </button>
                          )}
                        </>
                      ) : (
                        <div style={{ textAlign: "justify", lineHeight: "1.6", whiteSpace: "pre-wrap" }}>
                          {renderProcessingMessage()}
                          <br />
                          {localStorage.getItem('role') === 'admin' && (
                            <button
                              style={{
                                backgroundColor: "#007bff",
                                color: "white",
                                border: "none",
                                borderRadius: "6px",
                                padding: "8px 14px",
                                cursor: "pointer",
                              }}
                              disabled={isLoading || confirmEvidenceRequest}
                              onClick={() => onClickIdentifyEvidences(statement)}
                            >
                              Identify Evidence
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>


                  {/* ================= WITNESSES ================= */}
                  <tr>
                    <td className="label-col">Witnesses</td>
                    <td className="value-col">
                      {hasWitnesses ? (
                        <>
                          {statement.witnessList.map((w, i) => (
                            <div key={i} style={{ marginBottom: "15px" }}>
                              <div>
                                <strong>{i + 1}:</strong> {w.name || "-"}
                              </div>

                              <div style={{ color: "grey", marginTop: "5px", fontSize: "15px" }}>
                                Relevance: {w.relevance || "-"}
                              </div>
                            </div>
                          ))}

                          {localStorage.getItem('role') === 'admin' && (
                            <button
                              style={{
                                backgroundColor: "transparent",
                                color: "#007BFF",                   // blue (Bootstrap-style)
                                border: "none",
                                borderBottom: "1px solid #007BFF",  // line matches text color
                                padding: "4px 0 2px 0",
                                cursor: "pointer",
                                fontSize: "15px",
                                fontWeight: "500",
                                lineHeight: "1.2",
                                transition: "all 0.3s ease",
                              }}
                              disabled={isLoading || confirmWitnessRequest}
                              onClick={() => onClickIdentifyWitnesses(statement)}
                            >
                              Re-identify Witnesses
                            </button>
                          )}
                        </>
                      ) : (
                        <div style={{ textAlign: "justify", lineHeight: "1.6", whiteSpace: "pre-wrap" }}>
                          {renderProcessingMessage()}
                          <br />
                          {localStorage.getItem('role') === 'admin' && (
                            <button
                              style={{
                                backgroundColor: "#007bff",
                                color: "white",
                                border: "none",
                                borderRadius: "6px",
                                padding: "8px 14px",
                                cursor: "pointer",
                              }}
                              disabled={isLoading || confirmWitnessRequest}
                              onClick={() => onClickIdentifyWitnesses(statement)}
                            >
                              Identify Witnesses
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>


                  {/* ================= ACCUSED ================= */}
                  <tr>
                    <td className="label-col">Accused</td>
                    <td className="value-col">
                      {hasAccused ? (
                        <>
                          {statement.accusedList.map((a, i) => (
                            <div key={i} style={{ marginBottom: "15px" }}>
                              <div>
                                <strong>{i + 1}:</strong> {a.name || "-"}
                              </div>
                            </div>
                          ))}
                          {localStorage.getItem('role') === 'admin' && (
                            <button
                              style={{
                                backgroundColor: "transparent",
                                color: "#007BFF",                   // blue (Bootstrap-style)
                                border: "none",
                                borderBottom: "1px solid #007BFF",  // line matches text color
                                padding: "4px 0 2px 0",
                                cursor: "pointer",
                                fontSize: "15px",
                                fontWeight: "500",
                                lineHeight: "1.2",
                                transition: "all 0.3s ease",
                              }}
                              disabled={isLoading || confirmAccusedRequest}
                              onClick={() => onClickIdentifyAccused(statement)}
                            >
                              Re-identify Accused
                            </button>
                          )}
                        </>
                      ) : (
                        <div style={{ textAlign: "justify", lineHeight: "1.6", whiteSpace: "pre-wrap" }}>
                          {renderProcessingMessage()}
                          <br />
                          {localStorage.getItem('role') === 'admin' && (
                            <button
                              style={{
                                backgroundColor: "#007bff",
                                color: "white",
                                border: "none",
                                borderRadius: "6px",
                                padding: "8px 14px",
                                cursor: "pointer",
                              }}
                              disabled={isLoading || confirmAccusedRequest}
                              onClick={() => onClickIdentifyAccused(statement)}
                            >
                              Identify Accused
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>

                  {/* ================= FOLLOW-UP QUESTIONS ================= */}
                  <tr>
                    <td className="label-col">Follow-up Questions</td>
                    <td className="value-col">
                      {Array.isArray(statement.interrogationQuestions) &&
                        statement.interrogationQuestions.length > 0 ? (
                        <>
                          {statement.interrogationQuestions.map((q, i) => (
                            <div key={i} style={{ marginBottom: "15px" }}>
                              <div>
                                <strong>{i + 1}:</strong> {q.question || "-"}
                              </div>

                              <div
                                style={{
                                  color: "grey",
                                  marginTop: "5px",
                                  fontSize: "15px",
                                }}
                              >
                                Relevance: {q.relevance || "-"}
                              </div>
                            </div>
                          ))}
                          {localStorage.getItem('role') === 'admin' && (
                            <button
                              style={{
                                backgroundColor: "transparent",
                                color: "#007BFF",                   // blue (Bootstrap-style)
                                border: "none",
                                borderBottom: "1px solid #007BFF",  // line matches text color
                                padding: "4px 0 2px 0",
                                cursor: "pointer",
                                fontSize: "15px",
                                fontWeight: "500",
                                lineHeight: "1.2",
                                transition: "all 0.3s ease",
                              }}
                              disabled={isLoading || confirmFollowupRequest}
                              onClick={() => onClickIdentifyFollowupQuestions(statement)}
                            >
                              Re-identify Followup Questions
                            </button>
                          )}
                        </>

                      ) : (
                        <div style={{ textAlign: "justify", lineHeight: "1.6", whiteSpace: "pre-wrap" }}>
                          {renderProcessingMessage()}
                          <br />
                          {localStorage.getItem('role') === 'admin' && (
                            <button
                              style={{
                                backgroundColor: "#007bff",
                                color: "white",
                                border: "none",
                                borderRadius: "6px",
                                padding: "8px 14px",
                                cursor: "pointer",
                              }}
                              disabled={isLoading || confirmFollowupRequest}
                              onClick={() => onClickIdentifyFollowupQuestions(statement)}
                            >
                              Identify Followup Questions
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>

                </React.Fragment>
              );
            })}
          </tbody>
        </table>

        {errorMessage && (
          <div className="popup-overlay">
            <div className="error-popup"
              style={{
                border: '3px solid black',
                backgroundColor: 'white',
                padding: '20px',
                borderRadius: '8px',
                textAlign: 'center',
                maxWidth: '400px',
                margin: '20px auto'
              }}>
              <div className="error-message"
                style={{ marginBottom: '15px', fontSize: '16px', color: '#333' }}>
                {errorMessage}
              </div>
              <button className="error-ok-button"
                onClick={() => setErrorMessage(null)}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  color: 'white',
                  backgroundColor: '#007bff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              > OK </button>
            </div>
          </div>
        )}

        {confirmSummaryRequest && (<div className="popup-overlay"> <div className="popup-container"> <h3>Summarize Statement</h3> <p>Click Submit to generate Statement Summary.</p> <div className="popup-buttons"> <button onClick={onClose} className="cancel-button" disabled={isLoading}>Cancel</button> <button onClick={handleConfirmSummary} className="submit-popup-button" disabled={isLoading}> <div style={{ minHeight: "20px", display: "flex", alignItems: "center", justifyContent: "center" }}> {isLoading ? <div className="spinner"></div> : "Submit"} </div> </button> </div> {isLoading && (<div style={{ textAlign: 'center', marginTop: '20px', fontWeight: 'bold' }}> Please wait. It may take about a minute to do the analysis. </div>)} </div> </div>)}

        {confirmEventsRequest && (<div className="popup-overlay"> <div className="popup-container"> <h3>Extract Events & Timeline</h3> <p>Click Submit to extract Events and Timeline from Statement.</p> <div className="popup-buttons"> <button onClick={onClose} className="cancel-button" disabled={isLoading}>Cancel</button> <button onClick={handleConfirmEvents} className="submit-popup-button" disabled={isLoading}> <div style={{ minHeight: "20px", display: "flex", alignItems: "center", justifyContent: "center" }}> {isLoading ? <div className="spinner"></div> : "Submit"} </div> </button> </div> {isLoading && (<div style={{ textAlign: 'center', marginTop: '20px', fontWeight: 'bold' }}> Please wait. It may take about a minute to do the analysis. </div>)} </div> </div>)}

        {confirmEvidenceRequest && (<div className="popup-overlay"> <div className="popup-container"> <h3>Identify Evidences</h3> <p>Click Submit to identify Evidences from Statement.</p> <div className="popup-buttons"> <button onClick={onClose} className="cancel-button" disabled={isLoading}>Cancel</button> <button onClick={handleConfirmEvidences} className="submit-popup-button" disabled={isLoading}> <div style={{ minHeight: "20px", display: "flex", alignItems: "center", justifyContent: "center" }}> {isLoading ? <div className="spinner"></div> : "Submit"} </div> </button> </div> {isLoading && (<div style={{ textAlign: 'center', marginTop: '20px', fontWeight: 'bold' }}> Please wait. It may take about a minute to do the analysis. </div>)} </div> </div>)}

        {confirmWitnessRequest && (<div className="popup-overlay"> <div className="popup-container"> <h3>Identify Witnesses</h3> <p>Click Submit to identify Witnesses from Statement.</p> <div className="popup-buttons"> <button onClick={onClose} className="cancel-button" disabled={isLoading}>Cancel</button> <button onClick={handleConfirmWitnesses} className="submit-popup-button" disabled={isLoading}> <div style={{ minHeight: "20px", display: "flex", alignItems: "center", justifyContent: "center" }}> {isLoading ? <div className="spinner"></div> : "Submit"} </div> </button> </div> {isLoading && (<div style={{ textAlign: 'center', marginTop: '20px', fontWeight: 'bold' }}> Please wait. It may take about a minute to do the analysis. </div>)} </div> </div>)}

        {confirmAccusedRequest && (<div className="popup-overlay"> <div className="popup-container"> <h3>Identify Accused</h3> <p>Click Submit to identify Accused from Statement.</p> <div className="popup-buttons"> <button onClick={onClose} className="cancel-button" disabled={isLoading}>Cancel</button> <button onClick={handleConfirmAccused} className="submit-popup-button" disabled={isLoading}> <div style={{ minHeight: "20px", display: "flex", alignItems: "center", justifyContent: "center" }}> {isLoading ? <div className="spinner"></div> : "Submit"} </div> </button> </div> {isLoading && (<div style={{ textAlign: 'center', marginTop: '20px', fontWeight: 'bold' }}> Please wait. It may take about a minute to do the analysis. </div>)} </div> </div>)}

        {confirmFollowupRequest && (<div className="popup-overlay"> <div className="popup-container"> <h3>Identify Followup Questions</h3> <p>Click Submit to identify Followup Questions from Statement.</p> <div className="popup-buttons"> <button onClick={onClose} className="cancel-button" disabled={isLoading}>Cancel</button> <button onClick={handleConfirmFollowup} className="submit-popup-button" disabled={isLoading}> <div style={{ minHeight: "20px", display: "flex", alignItems: "center", justifyContent: "center" }}> {isLoading ? <div className="spinner"></div> : "Submit"} </div> </button> </div> {isLoading && (<div style={{ textAlign: 'center', marginTop: '20px', fontWeight: 'bold' }}> Please wait. It may take about a minute to do the analysis. </div>)} </div> </div>)}

        {statementText && (<div className="popup-overlay"> <div className="popup-container"> <h3>Statement</h3> <div style={{ fontSize: '16px', alignContent: 'justify', alignItems: 'justify', textAlign: 'justify', maxHeight: '50vh', overflowY: 'auto', marginBottom: '16px' }}> <p>{statementText.split('\n').map((line, index) => (<span key={index}> {line} <br /> </span>))}</p> </div> <div className="popup-buttons"> <button className="submit-popup-button" onClick={() => { setStatementText(null); }}>Close</button> </div> </div> </div>)}

        {isLoadingFIR && (
          <div className="popup-overlay" style={{ zIndex: 3000 }}>
            <div className="loader-container">
              <div className="loader"></div>
            </div>
          </div>
        )}
      </div>
    </TableSection>

  );
};

const AnomaliesTable = ({ fir }) => {
  const inconsistencies = fir.investigation?.inconsistencies || [];
  const contradictions = fir.investigation?.contradictions || [];

  // Determine how many rows to display
  const maxRows = Math.max(inconsistencies.length, contradictions.length);

  return (
    <TableSection title="">
      <div className="fir-table-container">
        <table className="fir-table">
          <thead>
            <tr>
              <th>Inconsistencies</th>
              <th>Contradictions</th>
            </tr>
          </thead>
          <tbody>
            {[...Array(maxRows)].map((_, index) => (
              <tr key={index}>
                <td>
                  {inconsistencies[index] ? `${index + 1}. ${inconsistencies[index]}` : "-"}
                </td>
                <td>
                  {contradictions[index] ? `${index + 1}. ${contradictions[index]}` : "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </TableSection>
  );
};

const InvestigateFIR = ({ API_URL }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [fir, setFir] = useState(location.state && location.state.fir ? location.state.fir : null);
  const selectedUser = fir ? fir.user : '';

  const section = location.state?.section;

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  //const [step, setStep] = useState(1);
  const [analysisButtonLabel, setAnalysisButtonLabel] = useState(null);
  const [isEndOfAnalysis, setIsEndOfAnalysis] = useState(true);
  const [isLongAnalysis, setIsLongAnalysis] = useState(true);

  useEffect(() => {
    if (!section && fir) {
      if (!fir.eventsAndTimeline) {
        setAnalysisButtonLabel('Generate Timeline');
        return;
      }

      if (!fir.urgentTasks) {
        setAnalysisButtonLabel('Identify Tasks');
        return;
      }

      if (!fir.evidenceList) {
        setAnalysisButtonLabel('Identify Evidences');
        // setStep(1);
        return;
      }

      if (!fir.witnessList) {
        setAnalysisButtonLabel('Identify Witnesses');
        // setStep(2);
        return;
      }

      setIsEndOfAnalysis(true);
    }
  }, []);

  const errorOK = () => {
    setErrorMessage(null);
    if (sessionExpired) {
      navigate('/login');
    }
  }

  const handlePrint = () => {
    // Add a style sheet to enforce minimum margins
    const style = document.createElement('style');
    style.innerHTML = `
    @page {
      margin-top: 5cm;
      margin-bottom: 1cm;
      margin-left: 1cm;
      margin-right: 1cm;
      scale: 0.9;
    }`;

    document.head.appendChild(style);
    window.print();
    document.head.removeChild(style);
  };

  const handleAnalyzeStatements = () => {
    navigate('/fir/statement', { state: { id: fir.id } });
  };

  const handleContinue = async () => {
    setIsLoading(true);

    var jwt = localStorage.getItem('jwtToken');
    try {
      if (analysisButtonLabel === 'Generate Timeline') {
        const response = await axios.get(API_URL + `/fir/events/${fir.id}`, {
          headers: {
            'Authorization': `Bearer ${jwt}`,
          },
        });
        setFir(response.data);
        setAnalysisButtonLabel('Identify Tasks');
      } else if (analysisButtonLabel === 'Identify Tasks') {
        const response = await axios.get(API_URL + `/fir/tasks/${fir.id}`, {
          headers: {
            'Authorization': `Bearer ${jwt}`,
          },
        });
        setFir(response.data);
        setAnalysisButtonLabel('Identify Evidences');
      } else if (analysisButtonLabel === 'Identify Evidences') {
        // if (step === 1) {
        const response = await axios.get(API_URL + `/fir/evidences/${fir.id}`, {
          headers: {
            'Authorization': `Bearer ${jwt}`,
          },
        });
        setFir(response.data);
        setAnalysisButtonLabel('Identify Witnesses');
        //setStep(2);
      } else if (analysisButtonLabel === 'Identify Witnesses') {
        //else if (step === 2) {
        const response = await axios.get(API_URL + `/fir/witnesses/${fir.id}`, {
          headers: {
            'Authorization': `Bearer ${jwt}`,
          },
        });
        setFir(response.data);
        setAnalysisButtonLabel(null);
        setIsEndOfAnalysis(true);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response && error.response.status === 401) {
        // Clear local storage and redirect to login
        localStorage.removeItem('jwtToken');
        localStorage.removeItem('email');
        localStorage.removeItem('role');
        localStorage.removeItem('firstName');
        localStorage.removeItem('firList');
        setSessionExpired(true);
        setErrorMessage("Your session has expired. Please log in again.");
      } else {
        setErrorMessage("An error occurred while processing the request. Please try again later.");
      }
    } finally {
      setIsLoading(false);
      localStorage.removeItem('firList');
    }
  };

  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Format seconds into MM:ss (e.g., 65 => "01:05")
  const formatElapsedTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    let timer;
    if (isLoading && isLongAnalysis) {
      timer = setInterval(() => {
        setElapsedSeconds(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isLoading, isLongAnalysis]);

  useEffect(() => {
    if (!isLoading) {
      setElapsedSeconds(0);
    }
  }, [isLoading]);

  const [isViewComplaintPopupOpen, setIsViewComplaintPopupOpen] = useState(false);

  return (
    <div className="fir-page-container">
      <Navbar />
      <main style={{ flex: 1 }}>
        <br></br><br></br>
        <div className="info-section" style={{ marginTop: '35px' }}>
          <center>
            <b>FIR Analysis for Investigation</b>
            <br />
            <span style={{ fontSize: "16px", fontWeight: "bold" }}>
              AI-generated. Verify the information before taking any action.
            </span>
          </center>
        </div>

        <div className="info-section">
          <center>
            <div style={{ color: '#c62828', fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' }}>
              You are using the free version. For a more detailed analysis, Monthly and Annual paid subscriptions are available for Police Stations and District Police. Contact 9582822225 for pricing details.
            </div>
            <button
              className='submit-button'
              style={{ marginLeft: 'auto', backgroundColor: '#FFFFFF', color: '#094268', fontWeight: 'bold', border: '1px solid #094268' }}
              onClick={() => setIsViewComplaintPopupOpen(true)}>
              View Complaint
            </button>
          </center>
        </div>

        <div className="fir-page">

          {section === null && (
            <>
              <div className="info-section">
                <p><b>FIR Sections:</b></p>
              </div>
              <FIRSections sections={fir.firSections || []} />

              <div className="info-section">
                <p><b>Complainant:</b></p>
              </div>
              <ComplainantTable complainant={fir.complainant || {}} />

              <div className="info-section">
                <p><b>Victim:</b></p>
              </div>
              <VictimTable victim={fir.victim || {}} />

              <div className="info-section">
                <p><b>Accused Persons:</b></p>
              </div>
              <AccusedTable accusedList={fir.accusedList || []} />
            </>
          )}

          {section && section === 'timeline' && fir.eventsAndTimeline && fir.eventsAndTimeline.length > 0 && (
            <>
              <div className="info-section">
                <p><b>Key Facts:</b></p>
              </div>
              <EventsTable fir={fir} />
              <div style={{ marginTop: '-25px', color: 'grey', fontSize: '18px' }}>
                <small>*NA = Not Available</small>
              </div>
            </>
          )}


          {section && section === 'tasks' && fir.urgentTasks && fir.urgentTasks.length > 0 && (
            <>
              <div className="info-section">
                <p><b>Suggested SOPs:</b></p>
              </div>
              <UrgentTasksTable API_URL={API_URL} fir={fir} updateFir={setFir} />
            </>
          )}

          {section && section === 'evidences' && fir.evidenceList && (
            <>
              <div className="info-section">
                <p><b>Potential Evidence List:</b></p>
              </div>
              <EvidenceTable API_URL={API_URL} fir={fir} updateFir={setFir} />
            </>
          )}

          {section && section === 'witnesses' && fir.witnessList && (
            <>
              <div className="info-section">
                <p><b>Potential Witness List:</b></p>
              </div>
              <WitnessTable API_URL={API_URL} fir={fir} updateFir={setFir} />
            </>
          )}

          {section && section === 'accused' && fir.accusedList && (
            <>
              <div className="info-section">
                <p><b>Accused:</b></p>
              </div>
              <AccusedIQTable API_URL={API_URL} fir={fir} updateFir={setFir} />
            </>
          )}

          {section === "statements" && (() => {
            const responses = fir?.investigation?.responses ?? []; // default to []
            return responses.length > 0 ? (
              <>
                <div className="info-section">
                  <p><b>Investigation Statements:</b></p>
                </div>
                <StatementTable API_URL={API_URL} fir={fir} updateFir={setFir} />
              </>
            ) : (
              <div className="info-section" style={{ marginTop: '60px', textAlign: 'center', fontSize: '16px', fontWeight: '600' }}>
                <p>No statements uploaded yet.</p>
              </div>
            );
          })()}

          {section === "discrepancies" && (() => {
            const inconsistencies = fir?.investigation?.inconsistencies ?? [];
            const contradictions = fir?.investigation?.contradictions ?? [];

            const hasData =
              inconsistencies.length > 0 || contradictions.length > 0;

            return hasData ? (
              <>
                <div className="info-section">
                  <p><b>Inconsistencies & Contradictions:</b></p>
                </div>
                <AnomaliesTable fir={fir} />
              </>
            ) : (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  //alignItems: "center",
                  //height: "60vh",
                  marginTop: '60px',
                  width: "100%",
                  textAlign: "center",
                  //color: "#6b7280",
                  fontSize: "16px",
                  fontWeight: '600',
                  //fontStyle: "italic",
                }}
              >
                <p>No statements uploaded yet. Upload statements to analyze inconsistencies and contradictions.</p>
              </div>
            );
          })()}


          {/* Footer action bar: Case Dashboard (left) + Actions (right) */}
          <div
            style={{
              marginTop: "14px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* Left: Case Dashboard */}
            <span
              onClick={() =>
                navigate("/fir/dashboard", { state: { fullFIR: fir, fir: fir } })
              }
              style={{
                cursor: "pointer",
                color: "#007AFF",
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "16px",
              }}
            >
              <span aria-hidden="true" style={{ fontSize: "18px", lineHeight: "14px" }}>
                ←
              </span>
              Case Dashboard
            </span>

            {/* Right: Action buttons */}
            <div style={{ display: "flex", alignItems: "center" }}>
              {section === "statements" && (
                <button
                  onClick={handleAnalyzeStatements}
                  disabled={isLoading}
                  hidden={isLoading}
                  className={`print-button ${isLoading ? "hidden" : ""}`}
                  style={{
                    backgroundColor: "#f5f5f5",
                    border: "1px solid #ccc",
                    borderRadius: "5px",
                    padding: "8px 14px",
                    cursor: "pointer",
                    marginRight: "12px",
                  }}
                >
                  Upload Statement
                </button>
              )}

              <button
                onClick={handlePrint}
                disabled={isLoading}
                hidden={isLoading}
                className={`print-button ${isLoading ? "hidden" : ""}`}
                style={{
                  backgroundColor: "#f5f5f5",
                  border: "1px solid #ccc",
                  borderRadius: "5px",
                  padding: "8px 14px",
                  cursor: "pointer",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height="20"
                  viewBox="0 0 24 24"
                  width="20"
                  fill="currentColor"
                  style={{ marginRight: "5px", verticalAlign: "middle" }}
                >
                  <path d="M0 0h24v24H0z" fill="none" />
                  <path d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 
          1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z" />
                </svg>
                Print
              </button>
            </div>
          </div>


          {/* Additional Messages */}
          {isLongAnalysis && (
            <div style={{ textAlign: 'center', marginBottom: '150px' }}>
              <p style={{ fontSize: '16px' }} hidden={!isLoading}>
                <strong>
                  {isLongAnalysis ? (
                    <>
                      Please wait. AI Agent is carefully analyzing FIR - this may take a{' '}
                      <span style={{ textDecoration: 'underline' }}>few minutes</span> depending on the complexity of the case.{' '}
                      <span style={{ fontWeight: 'bold', color: '#333' }}>
                        ({formatElapsedTime(elapsedSeconds)} mins)
                      </span>
                    </>
                  ) : (
                    "Please wait. AI Agent is carefully analyzing your case - this may take about a minute depending on the number of allegations involved."
                  )}
                </strong>
              </p>
            </div>
          )}

          <div style={{ textAlign: 'center', marginBottom: '50px' }}>
            <p style={{ fontSize: '16px' }}></p>
          </div>

        </div>

        {isViewComplaintPopupOpen && (
          <div className="popup-overlay">
            <div className="popup-container">
              <h3>Complaint Details</h3>
              <div style={{ fontSize: '16px', alignContent: 'justify', alignItems: 'justify', textAlign: 'justify', maxHeight: '50vh', overflowY: 'auto', marginBottom: '16px' }}>
                <p>{fir.firText.split('\n').map((line, index) => (
                  <span key={index}>
                    {line}
                    <br />
                  </span>
                ))}</p>
              </div>
              <div className="popup-buttons">
                <button
                  className="submit-popup-button"
                  onClick={() => {
                    setIsViewComplaintPopupOpen(false);
                  }
                  }>Close</button>
              </div>
            </div>
          </div>
        )}

        {errorMessage && (
          <div className="popup-overlay">
            <div className="popup-container">
              <h2>Error</h2>
              <p>{errorMessage}</p>
              <div className="popup-buttons">
                <button
                  className="submit-popup-button"
                  onClick={errorOK}
                >Close</button>
              </div>
            </div>
          </div>
        )}

        {sessionExpired && (
          <div className="popup-overlay">
            <div className="popup-container">
              <h2>Session Expired</h2>
              <p>Your session has expired. Please log in again.</p>
              <div className="popup-buttons">
                <button
                  className="submit-popup-button"
                  onClick={errorOK}
                >Close</button>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div >
  );
};

export default InvestigateFIR;